"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  Paper,
  useTheme,
  useMediaQuery,
  TablePagination,
  Divider,
  ListItem,
  ListItemText,
  ListItemIcon,
  List,
  InputAdornment,
  Skeleton,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Add,
  Edit,
  Delete,
  Search,
  Phone,
  Close,
  Badge,
  CalendarToday,
  Login,
  TaskAlt,
  PendingActions,
  VerifiedUser,
  ToggleOn,
  ToggleOff,
} from "@mui/icons-material";
import Layout from "../common/Layout";
import { useNotification } from "../../context/NotificationContext";
import apiClient from "../../context/apiClient";

// --- DEBOUNCE HOOK ---
// This custom hook delays updating a value until a specified time has passed
// without the value changing. This is used to prevent the search filter
// from running on every single keystroke.
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    // Set up a timer to update the debounced value after the delay
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    // Clean up the timer if the value changes (e.g., user types again)
    // or if the component unmounts. This is the core of the debounce logic.
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]); // Re-run the effect only if the value or delay changes

  return debouncedValue;
}

const DetailItem = ({ icon, primary, secondary }) => (
  <ListItem disablePadding>
    <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
      {icon}
    </ListItemIcon>
    <ListItemText
      primary={
        <Typography variant="body2" color="text.secondary">
          {primary}
        </Typography>
      }
      secondary={
        <Typography variant="body1" fontWeight={500} color="text.primary">
          {secondary || "N/A"}
        </Typography>
      }
    />
  </ListItem>
);

const UserManagement = () => {
  const { showNotification } = useNotification();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    user: null,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [newUser, setNewUser] = useState({
    username: "",
    email: "",
    password: "",
    user_role: "talent",
    phone_number: "",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);

  // Debounce the search term to avoid excessive re-renders and filtering
  const debouncedSearchTerm = useDebounce(searchTerm, 300); // 300ms delay

  const getRoleChipStyle = useCallback(
    (role) => {
      switch (role?.toLowerCase()) {
        case "talent":
          return {
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.dark || theme.palette.success.main,
          };
        case "employee":
        case "employer":
          return {
            bgcolor: alpha(theme.palette.warning.main, 0.1),
            color: theme.palette.warning.dark || theme.palette.warning.main,
          };
        case "admin":
          return {
            bgcolor: alpha(theme.palette.error.main, 0.1),
            color: theme.palette.error.dark || theme.palette.error.main,
          };
        default:
          return {
            bgcolor: alpha(theme.palette.text.secondary, 0.1),
            color: theme.palette.text.secondary,
          };
      }
    },
    [theme]
  );

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/admin/users/");
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "An error occurred while fetching users.";
      showNotification(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddUser = () => {
    setSelectedUser(null);
    setNewUser({
      username: "",
      email: "",
      password: "",
      user_role: "talent",
      phone_number: "",
    });
    setOpenDialog(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setNewUser({
      username: user.username,
      email: user.email,
      user_role: user.user_role.toLowerCase(),
      phone_number: user.phone_number || "",
      password: "",
    });
    setOpenDialog(true);
  };

  const handleViewUser = (user) => {
    setViewingUser(user);
    setViewUserDialogOpen(true);
  };

  const handleDeleteClick = (user) => setDeleteConfirm({ open: true, user });
  const handleCloseDeleteConfirm = () =>
    setDeleteConfirm({ open: false, user: null });

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.user) return;
    try {
      await apiClient.delete(`/admin/users/${deleteConfirm.user.id}/`);
      showNotification(
        `User "${deleteConfirm.user.username}" deleted successfully!`,
        "success"
      );
      handleCloseDeleteConfirm();
      fetchUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      showNotification(
        error.response?.data?.detail || "Failed to delete user.",
        "error"
      );
    }
  };

  const handleToggleUserStatus = async (user) => {
    if (!user) return;
    try {
      const newStatus = !user.is_active;
      await apiClient.patch(`/admin/users/${user.id}/`, {
        is_active: newStatus,
      });
      showNotification(
        `User "${user.username}" has been ${
          newStatus ? "activated" : "deactivated"
        }.`,
        "success"
      );
      fetchUsers();
    } catch (error) {
      console.error("Failed to update user status:", error);
      showNotification(
        error.response?.data?.detail || "Failed to update user status.",
        "error"
      );
    }
  };

  const handleSaveUser = async () => {
    try {
      const role = newUser.user_role.toUpperCase();
      if (selectedUser) {
        const payload = {
          username: newUser.username,
          email: newUser.email,
          user_role: role,
          phone_number: newUser.phone_number,
        };
        if (newUser.password) payload.password = newUser.password;
        await apiClient.patch(`/admin/users/${selectedUser.id}/`, payload);
        showNotification("User updated successfully!", "success");
      } else {
        if (!newUser.password) {
          showNotification("Password is required for new users.", "warning");
          return;
        }
        await apiClient.post("/admin/users/", { ...newUser, user_role: role });
        showNotification("User created successfully!", "success");
      }
      setOpenDialog(false);
      fetchUsers();
    } catch (error) {
      console.error("Failed to save user:", error);
      const errors = error.response?.data;
      let errorMessage = "An error occurred.";
      if (typeof errors === "object" && errors !== null) {
        errorMessage = Object.entries(errors)
          .map(
            ([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
          )
          .join(" | ");
      } else if (errors) {
        errorMessage = errors.toString();
      }
      showNotification(errorMessage, "error");
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Use the debounced search term for filtering.
  // useMemo ensures this expensive filtering operation only runs when the
  // user list or the debounced search term changes.
  const filteredUsers = useMemo(
    () =>
      users.filter(
        (user) =>
          user.username
            .toLowerCase()
            .includes(debouncedSearchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      ),
    [users, debouncedSearchTerm]
  );

  const userStats = {
    total: users.length,
    talent: users.filter((u) => u.user_role === "TALENT").length,
    employee: users.filter((u) => u.user_role === "EMPLOYER").length,
    admin: users.filter((u) => u.user_role === "ADMIN").length,
    active: users.filter((u) => u.is_active).length,
    pending: users.filter((u) => !u.is_active).length,
  };

  const sectionPaperStyles = {
    p: { xs: 2, md: 3 },
    borderRadius: "12px",
    backgroundColor: theme.palette.mint?.main || theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  };

  return (
    <Layout title="User Management">
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Stack spacing={3}>
          {/* ROW 1: HEADER */}
          <Paper
            elevation={0}
            sx={{
              ...sectionPaperStyles,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: theme.palette.primary.main }}
              >
                User Management
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage all users across the GATEP Platform
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleAddUser}
              sx={{ mt: { xs: 2, md: 0 } }}
            >
              Add New User
            </Button>
          </Paper>

          {/* ROW 2: SEARCH & FILTERS */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "12px",
              backgroundColor: theme.palette.mint?.main || theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems="center"
            >
              <TextField
                fullWidth
                placeholder="Search by username or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="disabled" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Paper>

          {/* ROW 3: STAT CARDS */}
          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, md: 2 },
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
          >
            {(loading
              ? [...Array(6)]
              : [
                  { title: "Total Users", value: userStats.total },
                  { title: "AI Talent", value: userStats.talent },
                  { title: "Employers", value: userStats.employee },
                  { title: "Admins", value: userStats.admin },
                  { title: "Active", value: userStats.active },
                  { title: "Inactive", value: userStats.pending },
                ]
            ).map((stat, index) => (
              <Box
                key={index}
                sx={{
                  flex: { xs: "1 1 45%", md: 1 },
                  minWidth: { xs: 150, md: 0 },
                }}
              >
                {loading ? (
                  <Skeleton
                    variant="rectangular"
                    height={120}
                    sx={{ borderRadius: "14px" }}
                  />
                ) : (
                  <Card
                    sx={{
                      position: "relative",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      height: 120,
                      borderRadius: "14px",
                      overflow: "hidden",
                      boxShadow: theme.shadows[2],
                    }}
                  >
                    <svg
                      width="100"
                      height="120"
                      viewBox="0 0 100 120"
                      style={{ position: "absolute", right: 0, top: 0 }}
                    >
                      <path
                        d="M40 0 L100 0 L100 120 C 80 120, 70 90, 70 60 C 70 30, 80 0, 40 0 Z"
                        fill={alpha(theme.palette.primary.light, 0.5)}
                      />
                    </svg>
                    <CardContent
                      sx={{
                        position: "relative",
                        zIndex: 1,
                        p: { xs: 1.5, md: 2 },
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: "0.75rem", md: "0.8rem" },
                          textTransform: "uppercase",
                          color: alpha(theme.palette.primary.contrastText, 0.8),
                        }}
                      >
                        {stat.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "2rem", md: "2.25rem" },
                          lineHeight: 1,
                        }}
                      >
                        {stat.value}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>
            ))}
          </Box>

          {/* ROW 4: USERS TABLE */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              backgroundColor: theme.palette.mint?.main || theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <TableContainer>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    {[
                      "User",
                      "Role",
                      "Status",
                      "Join Date",
                      "Last Login",
                      "Actions",
                    ].map((headCell) => (
                      <TableCell key={headCell}>{headCell}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading
                    ? Array.from(new Array(rowsPerPage)).map((_, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={2}
                            >
                              <Skeleton
                                variant="circular"
                                width={40}
                                height={40}
                              />
                              <Box>
                                <Skeleton width={80} />
                                <Skeleton width={120} />
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Skeleton width={80} height={24} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={70} height={24} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={90} />
                          </TableCell>
                          <TableCell>
                            <Skeleton width={90} />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row">
                              <Skeleton
                                variant="circular"
                                width={32}
                                height={32}
                              />
                              <Skeleton
                                variant="circular"
                                width={32}
                                height={32}
                              />
                              <Skeleton
                                variant="circular"
                                width={32}
                                height={32}
                              />
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    : filteredUsers
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((user) => (
                          <TableRow key={user.id} hover>
                            <TableCell>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleViewUser(user)}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: getRoleChipStyle(user.user_role)
                                      .color,
                                  }}
                                >
                                  {user.username.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={600}>
                                    {user.username}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {user.email}
                                  </Typography>
                                </Box>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.user_role}
                                size="small"
                                sx={{
                                  ...getRoleChipStyle(user.user_role),
                                  textTransform: "capitalize",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={user.is_active ? "Active" : "Inactive"}
                                size="small"
                                color={user.is_active ? "success" : "warning"}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {formatDate(user.date_joined)}
                            </TableCell>
                            <TableCell>{formatDate(user.last_login)}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0}>
                                <Tooltip
                                  title={
                                    user.is_active
                                      ? "Deactivate User"
                                      : "Activate User"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleUserStatus(user)}
                                    color={
                                      user.is_active ? "warning" : "success"
                                    }
                                  >
                                    {user.is_active ? (
                                      <ToggleOff />
                                    ) : (
                                      <ToggleOn />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit User">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditUser(user)}
                                  >
                                    <Edit fontSize="small" color="primary" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete User">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick(user)}
                                  >
                                    <Delete fontSize="small" color="error" />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                  {!loading && filteredUsers.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                        <Typography>No users found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </Stack>
      </Box>

      {/* DIALOGS */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{selectedUser ? "Edit User" : "Add New User"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={newUser.username}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, username: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={newUser.email}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, email: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={newUser.phone_number}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, phone_number: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                helperText={
                  selectedUser
                    ? "Leave blank to keep current password"
                    : "Required for new users"
                }
                value={newUser.password}
                onChange={(e) =>
                  setNewUser((p) => ({ ...p, password: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={newUser.user_role}
                  onChange={(e) =>
                    setNewUser((p) => ({ ...p, user_role: e.target.value }))
                  }
                  label="Role"
                >
                  <MenuItem value="talent">Talent</MenuItem>
                  <MenuItem value="employer">Employer</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSaveUser}>
            {selectedUser ? "Update User" : "Create User"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteConfirm.open}
        onClose={handleCloseDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the user "
            {deleteConfirm.user?.username}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      {viewingUser && (
        <Dialog
          open={viewUserDialogOpen}
          onClose={() => setViewUserDialogOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ p: 2, bgcolor: "background.paper" }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6" fontWeight={600}>
                User Details
              </Typography>
              <IconButton onClick={() => setViewUserDialogOpen(false)}>
                <Close />
              </IconButton>
            </Stack>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "action.hover" }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={0}
                  sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}
                >
                  <Avatar
                    sx={{
                      width: 100,
                      height: 100,
                      fontSize: "3rem",
                      mx: "auto",
                      mb: 2,
                      bgcolor: getRoleChipStyle(viewingUser.user_role).color,
                    }}
                  >
                    {viewingUser.username.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="h5" fontWeight={700}>
                    {viewingUser.username}
                  </Typography>
                  <Typography color="text.secondary" gutterBottom>
                    {viewingUser.email}
                  </Typography>
                  <Chip
                    label={viewingUser.user_role}
                    size="small"
                    sx={{
                      ...getRoleChipStyle(viewingUser.user_role),
                      fontWeight: 600,
                      borderRadius: "6px",
                      textTransform: "capitalize",
                      mt: 1,
                    }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                      Account Information
                    </Typography>
                    <List dense>
                      <DetailItem
                        icon={<Badge />}
                        primary="User ID"
                        secondary={viewingUser.id}
                      />
                      <DetailItem
                        icon={<Phone />}
                        primary="Phone Number"
                        secondary={viewingUser.phone_number}
                      />
                      <DetailItem
                        icon={
                          viewingUser.is_active ? (
                            <TaskAlt color="success" />
                          ) : (
                            <PendingActions color="warning" />
                          )
                        }
                        primary="Status"
                        secondary={
                          viewingUser.is_active
                            ? "Active"
                            : "Inactive / Pending"
                        }
                      />
                      <DetailItem
                        icon={<CalendarToday />}
                        primary="Date Joined"
                        secondary={formatDate(viewingUser.date_joined)}
                      />
                      <DetailItem
                        icon={<Login />}
                        primary="Last Login"
                        secondary={formatDate(viewingUser.last_login)}
                      />
                    </List>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                      Roles & Permissions
                    </Typography>
                    <List dense>
                      <DetailItem
                        icon={
                          viewingUser.is_staff ? (
                            <TaskAlt color="success" />
                          ) : (
                            <PendingActions color="error" />
                          )
                        }
                        primary="Staff Access (Admin)"
                        secondary={viewingUser.is_staff ? "Yes" : "No"}
                      />
                      <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <VerifiedUser />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" color="text.secondary">
                              User Permissions
                            </Typography>
                          }
                          secondary={
                            viewingUser.user_permissions.length > 0 ? (
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                mt={0.5}
                              >
                                {viewingUser.user_permissions.map((p) => (
                                  <Chip key={p} label={p} size="small" />
                                ))}
                              </Stack>
                            ) : (
                              "None"
                            )
                          }
                        />
                      </ListItem>
                    </List>
                  </Paper>
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
};

export default UserManagement;


// "use client";

// import { useState, useEffect, useCallback, useRef } from "react";
// import {
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Box,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Avatar,
//   IconButton,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   TextField,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Chip,
//   Stack,
//   Paper,
//   useTheme,
//   useMediaQuery,
//   TablePagination,
//   Divider,
//   ListItem,
//   ListItemText,
//   ListItemIcon,
//   List,
//   InputAdornment,
//   Skeleton,
//   Tooltip,
// } from "@mui/material";
// import { alpha } from "@mui/material/styles";
// import {
//   Add,
//   Edit,
//   Delete,
//   Search,
//   Phone,
//   Close,
//   Badge,
//   CalendarToday,
//   Login,
//   TaskAlt,
//   PendingActions,
//   VerifiedUser,
//   ToggleOn,
//   ToggleOff,
// } from "@mui/icons-material";
// import Layout from "../common/Layout";
// import { useNotification } from "../../context/NotificationContext";
// import apiClient from "../../context/apiClient";

// const DetailItem = ({ icon, primary, secondary }) => (
//   <ListItem disablePadding>
//     <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
//       {icon}
//     </ListItemIcon>
//     <ListItemText
//       primary={
//         <Typography variant="body2" color="text.secondary">
//           {primary}
//         </Typography>
//       }
//       secondary={
//         <Typography variant="body1" fontWeight={500} color="text.primary">
//           {secondary || "N/A"}
//         </Typography>
//       }
//     />
//   </ListItem>
// );

// const UserManagement = () => {
//   const { showNotification } = useNotification();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//   const [users, setUsers] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [openDialog, setOpenDialog] = useState(false);
//   const [deleteConfirm, setDeleteConfirm] = useState({
//     open: false,
//     user: null,
//   });
//   const [selectedUser, setSelectedUser] = useState(null);
//   const [searchTerm, setSearchTerm] = useState("");
//   const [newUser, setNewUser] = useState({
//     username: "",
//     email: "",
//     password: "",
//     user_role: "talent",
//     phone_number: "",
//   });
//   const [page, setPage] = useState(0);
//   const [rowsPerPage, setRowsPerPage] = useState(5);
//   const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
//   const [viewingUser, setViewingUser] = useState(null);

//   const getRoleChipStyle = useCallback(
//     (role) => {
//       switch (role?.toLowerCase()) {
//         case "talent":
//           return {
//             bgcolor: alpha(theme.palette.success.main, 0.1),
//             color: theme.palette.success.dark || theme.palette.success.main,
//           };
//         case "employee":
//         case "employer":
//           return {
//             bgcolor: alpha(theme.palette.warning.main, 0.1),
//             color: theme.palette.warning.dark || theme.palette.warning.main,
//           };
//         case "admin":
//           return {
//             bgcolor: alpha(theme.palette.error.main, 0.1),
//             color: theme.palette.error.dark || theme.palette.error.main,
//           };
//         default:
//           return {
//             bgcolor: alpha(theme.palette.text.secondary, 0.1),
//             color: theme.palette.text.secondary,
//           };
//       }
//     },
//     [theme]
//   );

//   const formatDate = (isoString) => {
//     if (!isoString) return "N/A";
//     const date = new Date(isoString);
//     return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
//   };

//   const fetchUsers = useCallback(async () => {
//     setLoading(true);
//     try {
//       const response = await apiClient.get("/admin/users/");
//       setUsers(response.data);
//     } catch (error) {
//       console.error("Failed to fetch users:", error);
//       const errorMessage =
//         error.response?.data?.detail ||
//         "An error occurred while fetching users.";
//       showNotification(errorMessage, "error");
//     } finally {
//       setLoading(false);
//     }
//   }, [showNotification]);

//   useEffect(() => {
//     fetchUsers();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const handleAddUser = () => {
//     setSelectedUser(null);
//     setNewUser({
//       username: "",
//       email: "",
//       password: "",
//       user_role: "talent",
//       phone_number: "",
//     });
//     setOpenDialog(true);
//   };

//   const handleEditUser = (user) => {
//     setSelectedUser(user);
//     setNewUser({
//       username: user.username,
//       email: user.email,
//       user_role: user.user_role.toLowerCase(),
//       phone_number: user.phone_number || "",
//       password: "",
//     });
//     setOpenDialog(true);
//   };

//   const handleViewUser = (user) => {
//     setViewingUser(user);
//     setViewUserDialogOpen(true);
//   };

//   const handleDeleteClick = (user) => setDeleteConfirm({ open: true, user });
//   const handleCloseDeleteConfirm = () =>
//     setDeleteConfirm({ open: false, user: null });

//   const handleDeleteConfirm = async () => {
//     if (!deleteConfirm.user) return;
//     try {
//       await apiClient.delete(`/admin/users/${deleteConfirm.user.id}/`);
//       showNotification(
//         `User "${deleteConfirm.user.username}" deleted successfully!`,
//         "success"
//       );
//       handleCloseDeleteConfirm();
//       fetchUsers();
//     } catch (error) {
//       console.error("Failed to delete user:", error);
//       showNotification(
//         error.response?.data?.detail || "Failed to delete user.",
//         "error"
//       );
//     }
//   };

//   const handleToggleUserStatus = async (user) => {
//     if (!user) return;
//     try {
//       const newStatus = !user.is_active;
//       await apiClient.patch(`/admin/users/${user.id}/`, {
//         is_active: newStatus,
//       });
//       showNotification(
//         `User "${user.username}" has been ${
//           newStatus ? "activated" : "deactivated"
//         }.`,
//         "success"
//       );
//       fetchUsers();
//     } catch (error) {
//       console.error("Failed to update user status:", error);
//       showNotification(
//         error.response?.data?.detail || "Failed to update user status.",
//         "error"
//       );
//     }
//   };

//   const handleSaveUser = async () => {
//     try {
//       const role = newUser.user_role.toUpperCase();
//       if (selectedUser) {
//         const payload = {
//           username: newUser.username,
//           email: newUser.email,
//           user_role: role,
//           phone_number: newUser.phone_number,
//         };
//         if (newUser.password) payload.password = newUser.password;
//         await apiClient.patch(`/admin/users/${selectedUser.id}/`, payload);
//         showNotification("User updated successfully!", "success");
//       } else {
//         if (!newUser.password) {
//           showNotification("Password is required for new users.", "warning");
//           return;
//         }
//         await apiClient.post("/admin/users/", { ...newUser, user_role: role });
//         showNotification("User created successfully!", "success");
//       }
//       setOpenDialog(false);
//       fetchUsers();
//     } catch (error) {
//       console.error("Failed to save user:", error);
//       const errors = error.response?.data;
//       let errorMessage = "An error occurred.";
//       if (typeof errors === "object" && errors !== null) {
//         errorMessage = Object.entries(errors)
//           .map(
//             ([key, value]) =>
//               `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
//           )
//           .join(" | ");
//       } else if (errors) {
//         errorMessage = errors.toString();
//       }
//       showNotification(errorMessage, "error");
//     }
//   };

//   const handleChangePage = (event, newPage) => setPage(newPage);
//   const handleChangeRowsPerPage = (event) => {
//     setRowsPerPage(parseInt(event.target.value, 10));
//     setPage(0);
//   };

//   const filteredUsers = users.filter(
//     (user) =>
//       user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       user.email.toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   const userStats = {
//     total: users.length,
//     talent: users.filter((u) => u.user_role === "TALENT").length,
//     employee: users.filter((u) => u.user_role === "EMPLOYER").length,
//     admin: users.filter((u) => u.user_role === "ADMIN").length,
//     active: users.filter((u) => u.is_active).length,
//     pending: users.filter((u) => !u.is_active).length,
//   };

//   const sectionPaperStyles = {
//     p: { xs: 2, md: 3 },
//     borderRadius: "12px",
//     backgroundColor: theme.palette.mint.main,
//     boxShadow: theme.shadows[1],
//   };

//   return (
//     <Layout title="User Management">
//       <Box
//         sx={{
//           p: { xs: 2, md: 3 },
//           backgroundColor: theme.palette.background.default,
//         }}
//       >
//         <Stack spacing={3}>
//           {/* ROW 1: HEADER */}
//           <Paper
//             elevation={0}
//             sx={{
//               ...sectionPaperStyles,
//               display: "flex",
//               flexDirection: { xs: "column", md: "row" },
//               justifyContent: "space-between",
//               alignItems: { xs: "flex-start", md: "center" },
//             }}
//           >
//             <Box>
//               <Typography
//                 variant="h4"
//                 sx={{ fontWeight: 700, color: theme.palette.primary.main }}
//               >
//                 User Management
//               </Typography>
//               <Typography variant="body1" color="text.secondary">
//                 Manage all users across the GATEP Platform
//               </Typography>
//             </Box>
//             <Button
//               variant="contained"
//               color="primary"
//               startIcon={<Add />}
//               onClick={handleAddUser}
//               sx={{ mt: { xs: 2, md: 0 } }}
//             >
//               Add New User
//             </Button>
//           </Paper>

//           {/* ROW 2: SEARCH & FILTERS */}
//           <Paper
//             elevation={0}
//             sx={{
//               p: 2,
//               borderRadius: "12px",
//               backgroundColor: "background.paper",
//               border: `1px solid ${theme.palette.divider}`,
//             }}
//           >
//             <Stack
//               direction={{ xs: "column", md: "row" }}
//               spacing={2}
//               alignItems="center"
//             >
//               <TextField
//                 fullWidth
//                 placeholder="Search by username or email"
//                 value={searchTerm}
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 InputProps={{
//                   startAdornment: (
//                     <InputAdornment position="start">
//                       <Search color="disabled" />
//                     </InputAdornment>
//                   ),
//                 }}
//               />
//             </Stack>
//           </Paper>

//           {/* ROW 3: STAT CARDS */}
//           <Box
//             sx={{
//               display: "flex",
//               gap: { xs: 1.5, md: 2 },
//               flexWrap: { xs: "wrap", md: "nowrap" },
//             }}
//           >
//             {(loading
//               ? [...Array(6)]
//               : [
//                   { title: "Total Users", value: userStats.total },
//                   { title: "AI Talent", value: userStats.talent },
//                   { title: "Employers", value: userStats.employee },
//                   { title: "Admins", value: userStats.admin },
//                   { title: "Active", value: userStats.active },
//                   { title: "Inactive", value: userStats.pending },
//                 ]
//             ).map((stat, index) => (
//               <Box
//                 key={index}
//                 sx={{
//                   flex: { xs: "1 1 45%", md: 1 },
//                   minWidth: { xs: 150, md: 0 },
//                 }}
//               >
//                 {loading ? (
//                   <Skeleton
//                     variant="rectangular"
//                     height={120}
//                     sx={{ borderRadius: "14px" }}
//                   />
//                 ) : (
//                   <Card
//                     sx={{
//                       position: "relative",
//                       backgroundColor: theme.palette.primary.main,
//                       color: theme.palette.primary.contrastText,
//                       height: 120,
//                       borderRadius: "14px",
//                       overflow: "hidden",
//                       boxShadow: theme.shadows[2],
//                     }}
//                   >
//                     <svg
//                       width="100"
//                       height="120"
//                       viewBox="0 0 100 120"
//                       style={{ position: "absolute", right: 0, top: 0 }}
//                     >
//                       <path
//                         d="M40 0 L100 0 L100 120 C 80 120, 70 90, 70 60 C 70 30, 80 0, 40 0 Z"
//                         fill={theme.palette.accent.main}
//                       />
//                     </svg>
//                     <CardContent
//                       sx={{
//                         position: "relative",
//                         zIndex: 1,
//                         p: { xs: 1.5, md: 2 },
//                         display: "flex",
//                         flexDirection: "column",
//                         justifyContent: "space-between",
//                         height: "100%",
//                       }}
//                     >
//                       <Typography
//                         sx={{
//                           fontWeight: 600,
//                           fontSize: { xs: "0.75rem", md: "0.8rem" },
//                           textTransform: "uppercase",
//                           color: alpha(theme.palette.primary.contrastText, 0.8),
//                         }}
//                       >
//                         {stat.title}
//                       </Typography>
//                       <Typography
//                         sx={{
//                           fontWeight: "bold",
//                           fontSize: { xs: "2rem", md: "2.25rem" },
//                           lineHeight: 1,
//                         }}
//                       >
//                         {stat.value}
//                       </Typography>
//                     </CardContent>
//                   </Card>
//                 )}
//               </Box>
//             ))}
//           </Box>

//           {/* ROW 4: USERS TABLE */}
//           <Paper
//             elevation={0}
//             sx={{
//               borderRadius: "16px",
//               overflow: "hidden",
//               backgroundColor: "background.paper",
//               border: `1px solid ${theme.palette.divider}`,
//             }}
//           >
//             <TableContainer>
//               <Table sx={{ minWidth: 800 }}>
//                 <TableHead>
//                   <TableRow>
//                     {[
//                       "User",
//                       "Role",
//                       "Status",
//                       "Join Date",
//                       "Last Login",
//                       "Actions",
//                     ].map((headCell) => (
//                       <TableCell key={headCell}>{headCell}</TableCell>
//                     ))}
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {loading
//                     ? Array.from(new Array(rowsPerPage)).map((_, index) => (
//                         <TableRow key={index}>
//                           <TableCell>
//                             <Stack
//                               direction="row"
//                               alignItems="center"
//                               spacing={2}
//                             >
//                               <Skeleton
//                                 variant="circular"
//                                 width={40}
//                                 height={40}
//                               />
//                               <Box>
//                                 <Skeleton width={80} />
//                                 <Skeleton width={120} />
//                               </Box>
//                             </Stack>
//                           </TableCell>
//                           <TableCell>
//                             <Skeleton width={80} height={24} />
//                           </TableCell>
//                           <TableCell>
//                             <Skeleton width={70} height={24} />
//                           </TableCell>
//                           <TableCell>
//                             <Skeleton width={90} />
//                           </TableCell>
//                           <TableCell>
//                             <Skeleton width={90} />
//                           </TableCell>
//                           <TableCell>
//                             <Stack direction="row">
//                               <Skeleton
//                                 variant="circular"
//                                 width={32}
//                                 height={32}
//                               />
//                               <Skeleton
//                                 variant="circular"
//                                 width={32}
//                                 height={32}
//                               />
//                               <Skeleton
//                                 variant="circular"
//                                 width={32}
//                                 height={32}
//                               />
//                             </Stack>
//                           </TableCell>
//                         </TableRow>
//                       ))
//                     : filteredUsers
//                         .slice(
//                           page * rowsPerPage,
//                           page * rowsPerPage + rowsPerPage
//                         )
//                         .map((user) => (
//                           <TableRow key={user.id} hover>
//                             <TableCell>
//                               <Stack
//                                 direction="row"
//                                 alignItems="center"
//                                 spacing={2}
//                                 sx={{ cursor: "pointer" }}
//                                 onClick={() => handleViewUser(user)}
//                               >
//                                 <Avatar
//                                   sx={{
//                                     bgcolor: getRoleChipStyle(user.user_role)
//                                       .color,
//                                   }}
//                                 >
//                                   {user.username.charAt(0).toUpperCase()}
//                                 </Avatar>
//                                 <Box>
//                                   <Typography variant="body2" fontWeight={600}>
//                                     {user.username}
//                                   </Typography>
//                                   <Typography
//                                     variant="caption"
//                                     color="text.secondary"
//                                   >
//                                     {user.email}
//                                   </Typography>
//                                 </Box>
//                               </Stack>
//                             </TableCell>
//                             <TableCell>
//                               <Chip
//                                 label={user.user_role}
//                                 size="small"
//                                 sx={{
//                                   ...getRoleChipStyle(user.user_role),
//                                   textTransform: "capitalize",
//                                 }}
//                               />
//                             </TableCell>
//                             <TableCell>
//                               <Chip
//                                 label={user.is_active ? "Active" : "Inactive"}
//                                 size="small"
//                                 color={user.is_active ? "success" : "warning"}
//                                 variant="outlined"
//                               />
//                             </TableCell>
//                             <TableCell>
//                               {formatDate(user.date_joined)}
//                             </TableCell>
//                             <TableCell>{formatDate(user.last_login)}</TableCell>
//                             <TableCell>
//                               <Stack direction="row" spacing={0}>
//                                 <Tooltip
//                                   title={
//                                     user.is_active
//                                       ? "Deactivate User"
//                                       : "Activate User"
//                                   }
//                                 >
//                                   <IconButton
//                                     size="small"
//                                     onClick={() => handleToggleUserStatus(user)}
//                                     color={
//                                       user.is_active ? "warning" : "success"
//                                     }
//                                   >
//                                     {user.is_active ? (
//                                       <ToggleOff />
//                                     ) : (
//                                       <ToggleOn />
//                                     )}
//                                   </IconButton>
//                                 </Tooltip>
//                                 <Tooltip title="Edit User">
//                                   <IconButton
//                                     size="small"
//                                     onClick={() => handleEditUser(user)}
//                                   >
//                                     <Edit fontSize="small" color="primary" />
//                                   </IconButton>
//                                 </Tooltip>
//                                 <Tooltip title="Delete User">
//                                   <IconButton
//                                     size="small"
//                                     onClick={() => handleDeleteClick(user)}
//                                   >
//                                     <Delete fontSize="small" color="error" />
//                                   </IconButton>
//                                 </Tooltip>
//                               </Stack>
//                             </TableCell>
//                           </TableRow>
//                         ))}
//                   {!loading && filteredUsers.length === 0 && (
//                     <TableRow>
//                       <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
//                         <Typography>No users found.</Typography>
//                       </TableCell>
//                     </TableRow>
//                   )}
//                 </TableBody>
//               </Table>
//             </TableContainer>
//             <TablePagination
//               rowsPerPageOptions={[5, 10, 25, 50]}
//               component="div"
//               count={filteredUsers.length}
//               rowsPerPage={rowsPerPage}
//               page={page}
//               onPageChange={handleChangePage}
//               onRowsPerPageChange={handleChangeRowsPerPage}
//             />
//           </Paper>
//         </Stack>
//       </Box>

//       {/* DIALOGS */}
//       <Dialog
//         open={openDialog}
//         onClose={() => setOpenDialog(false)}
//         maxWidth="sm"
//         fullWidth
//         fullScreen={isMobile}
//       >
//         <DialogTitle>{selectedUser ? "Edit User" : "Add New User"}</DialogTitle>
//         <DialogContent>
//           <Grid container spacing={2} sx={{ mt: 1 }}>
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Username"
//                 value={newUser.username}
//                 onChange={(e) =>
//                   setNewUser((p) => ({ ...p, username: e.target.value }))
//                 }
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Email Address"
//                 type="email"
//                 value={newUser.email}
//                 onChange={(e) =>
//                   setNewUser((p) => ({ ...p, email: e.target.value }))
//                 }
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Phone Number"
//                 value={newUser.phone_number}
//                 onChange={(e) =>
//                   setNewUser((p) => ({ ...p, phone_number: e.target.value }))
//                 }
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <TextField
//                 fullWidth
//                 label="Password"
//                 type="password"
//                 helperText={
//                   selectedUser
//                     ? "Leave blank to keep current password"
//                     : "Required for new users"
//                 }
//                 value={newUser.password}
//                 onChange={(e) =>
//                   setNewUser((p) => ({ ...p, password: e.target.value }))
//                 }
//               />
//             </Grid>
//             <Grid item xs={12}>
//               <FormControl fullWidth>
//                 <InputLabel>Role</InputLabel>
//                 <Select
//                   value={newUser.user_role}
//                   onChange={(e) =>
//                     setNewUser((p) => ({ ...p, user_role: e.target.value }))
//                   }
//                   label="Role"
//                 >
//                   <MenuItem value="talent">Talent</MenuItem>
//                   <MenuItem value="employer">Employer</MenuItem>
//                   <MenuItem value="admin">Admin</MenuItem>
//                 </Select>
//               </FormControl>
//             </Grid>
//           </Grid>
//         </DialogContent>
//         <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
//           <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
//           <Button variant="contained" color="primary" onClick={handleSaveUser}>
//             {selectedUser ? "Update User" : "Create User"}
//           </Button>
//         </DialogActions>
//       </Dialog>
//       <Dialog
//         open={deleteConfirm.open}
//         onClose={handleCloseDeleteConfirm}
//         maxWidth="xs"
//         fullWidth
//       >
//         <DialogTitle>Confirm Deletion</DialogTitle>
//         <DialogContent>
//           <Typography>
//             Are you sure you want to delete the user "
//             {deleteConfirm.user?.username}"? This action cannot be undone.
//           </Typography>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
//           <Button
//             variant="contained"
//             color="error"
//             onClick={handleDeleteConfirm}
//           >
//             Delete
//           </Button>
//         </DialogActions>
//       </Dialog>
//       {viewingUser && (
//         <Dialog
//           open={viewUserDialogOpen}
//           onClose={() => setViewUserDialogOpen(false)}
//           maxWidth="md"
//           fullWidth
//           fullScreen={isMobile}
//         >
//           <DialogTitle sx={{ p: 2, bgcolor: "background.paper" }}>
//             <Stack
//               direction="row"
//               justifyContent="space-between"
//               alignItems="center"
//             >
//               <Typography variant="h6" fontWeight={600}>
//                 User Details
//               </Typography>
//               <IconButton onClick={() => setViewUserDialogOpen(false)}>
//                 <Close />
//               </IconButton>
//             </Stack>
//           </DialogTitle>
//           <Divider />
//           <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "action.hover" }}>
//             <Grid container spacing={3}>
//               <Grid item xs={12} md={4}>
//                 <Paper
//                   elevation={0}
//                   sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}
//                 >
//                   <Avatar
//                     sx={{
//                       width: 100,
//                       height: 100,
//                       fontSize: "3rem",
//                       mx: "auto",
//                       mb: 2,
//                       bgcolor: getRoleChipStyle(viewingUser.user_role).color,
//                     }}
//                   >
//                     {viewingUser.username.charAt(0).toUpperCase()}
//                   </Avatar>
//                   <Typography variant="h5" fontWeight={700}>
//                     {viewingUser.username}
//                   </Typography>
//                   <Typography color="text.secondary" gutterBottom>
//                     {viewingUser.email}
//                   </Typography>
//                   <Chip
//                     label={viewingUser.user_role}
//                     size="small"
//                     sx={{
//                       ...getRoleChipStyle(viewingUser.user_role),
//                       fontWeight: 600,
//                       borderRadius: "6px",
//                       textTransform: "capitalize",
//                       mt: 1,
//                     }}
//                   />
//                 </Paper>
//               </Grid>
//               <Grid item xs={12} md={8}>
//                 <Stack spacing={3}>
//                   <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
//                     <Typography variant="h6" fontWeight={600} mb={2}>
//                       Account Information
//                     </Typography>
//                     <List dense>
//                       <DetailItem
//                         icon={<Badge />}
//                         primary="User ID"
//                         secondary={viewingUser.id}
//                       />
//                       <DetailItem
//                         icon={<Phone />}
//                         primary="Phone Number"
//                         secondary={viewingUser.phone_number}
//                       />
//                       <DetailItem
//                         icon={
//                           viewingUser.is_active ? (
//                             <TaskAlt color="success" />
//                           ) : (
//                             <PendingActions color="warning" />
//                           )
//                         }
//                         primary="Status"
//                         secondary={
//                           viewingUser.is_active
//                             ? "Active"
//                             : "Inactive / Pending"
//                         }
//                       />
//                       <DetailItem
//                         icon={<CalendarToday />}
//                         primary="Date Joined"
//                         secondary={formatDate(viewingUser.date_joined)}
//                       />
//                       <DetailItem
//                         icon={<Login />}
//                         primary="Last Login"
//                         secondary={formatDate(viewingUser.last_login)}
//                       />
//                     </List>
//                   </Paper>
//                   <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
//                     <Typography variant="h6" fontWeight={600} mb={2}>
//                       Roles & Permissions
//                     </Typography>
//                     <List dense>
//                       <DetailItem
//                         icon={
//                           viewingUser.is_staff ? (
//                             <TaskAlt color="success" />
//                           ) : (
//                             <PendingActions color="error" />
//                           )
//                         }
//                         primary="Staff Access (Admin)"
//                         secondary={viewingUser.is_staff ? "Yes" : "No"}
//                       />
//                       <ListItem disablePadding>
//                         <ListItemIcon sx={{ minWidth: 40 }}>
//                           <VerifiedUser />
//                         </ListItemIcon>
//                         <ListItemText
//                           primary={
//                             <Typography variant="body2" color="text.secondary">
//                               User Permissions
//                             </Typography>
//                           }
//                           secondary={
//                             viewingUser.user_permissions.length > 0 ? (
//                               <Stack
//                                 direction="row"
//                                 spacing={1}
//                                 flexWrap="wrap"
//                                 mt={0.5}
//                               >
//                                 {viewingUser.user_permissions.map((p) => (
//                                   <Chip key={p} label={p} size="small" />
//                                 ))}
//                               </Stack>
//                             ) : (
//                               "None"
//                             )
//                           }
//                         />
//                       </ListItem>
//                     </List>
//                   </Paper>
//                 </Stack>
//               </Grid>
//             </Grid>
//           </DialogContent>
//         </Dialog>
//       )}
//     </Layout>
//   );
// };

// export default UserManagement;
// // "use client";

// // import { useState, useEffect, useCallback } from "react";
// // import {
// //   Grid,
// //   Card,
// //   CardContent,
// //   Typography,
// //   Button,
// //   Box,
// //   Table,
// //   TableBody,
// //   TableCell,
// //   TableContainer,
// //   TableHead,
// //   TableRow,
// //   Avatar,
// //   IconButton,
// //   Dialog,
// //   DialogTitle,
// //   DialogContent,
// //   DialogActions,
// //   TextField,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   MenuItem,
// //   Chip,
// //   Stack,
// //   Paper,
// //   useTheme,
// //   useMediaQuery,
// //   TablePagination,
// //   Divider,
// //   ListItem,
// //   ListItemText,
// //   ListItemIcon,
// //   List,
// //   InputAdornment,
// //   Skeleton,
// //   Tooltip,
// // } from "@mui/material";
// // import { alpha } from "@mui/material/styles";
// // import {
// //   Add,
// //   Edit,
// //   Delete,
// //   Search,
// //   Phone,
// //   Close,
// //   Badge,
// //   CalendarToday,
// //   Login,
// //   TaskAlt,
// //   PendingActions,
// //   VerifiedUser,
// //   ToggleOn,
// //   ToggleOff,
// // } from "@mui/icons-material";
// // import Layout from "../common/Layout";
// // import { useNotification } from "../../context/NotificationContext";
// // import apiClient from "../../context/apiClient";

// // const DetailItem = ({ icon, primary, secondary }) => (
// //   <ListItem disablePadding>
// //     <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
// //       {icon}
// //     </ListItemIcon>
// //     <ListItemText
// //       primary={
// //         <Typography variant="body2" color="text.secondary">
// //           {primary}
// //         </Typography>
// //       }
// //       secondary={
// //         <Typography variant="body1" fontWeight={500} color="text.primary">
// //           {secondary || "N/A"}
// //         </Typography>
// //       }
// //     />
// //   </ListItem>
// // );

// // const UserManagement = () => {
// //   const { showNotification } = useNotification();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

// //   const [users, setUsers] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [openDialog, setOpenDialog] = useState(false);
// //   const [deleteConfirm, setDeleteConfirm] = useState({
// //     open: false,
// //     user: null,
// //   });
// //   const [selectedUser, setSelectedUser] = useState(null);
// //   const [searchTerm, setSearchTerm] = useState("");
// //   const [newUser, setNewUser] = useState({
// //     username: "",
// //     email: "",
// //     password: "",
// //     user_role: "talent",
// //     phone_number: "",
// //   });
// //   const [page, setPage] = useState(0);
// //   const [rowsPerPage, setRowsPerPage] = useState(5);
// //   const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
// //   const [viewingUser, setViewingUser] = useState(null);

// //   const getRoleChipStyle = useCallback(
// //     (role) => {
// //       switch (role?.toLowerCase()) {
// //         case "talent":
// //           return {
// //             bgcolor: alpha(theme.palette.success.main, 0.1),
// //             color: theme.palette.success.dark || theme.palette.success.main,
// //           };
// //         case "employee":
// //         case "employer":
// //           return {
// //             bgcolor: alpha(theme.palette.warning.main, 0.1),
// //             color: theme.palette.warning.dark || theme.palette.warning.main,
// //           };
// //         case "admin":
// //           return {
// //             bgcolor: alpha(theme.palette.error.main, 0.1),
// //             color: theme.palette.error.dark || theme.palette.error.main,
// //           };
// //         default:
// //           return {
// //             bgcolor: alpha(theme.palette.text.secondary, 0.1),
// //             color: theme.palette.text.secondary,
// //           };
// //       }
// //     },
// //     [theme]
// //   );

// //   const formatDate = (isoString) => {
// //     if (!isoString) return "N/A";
// //     const date = new Date(isoString);
// //     return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
// //   };

// //   const fetchUsers = useCallback(async () => {
// //     setLoading(true);
// //     try {
// //       const response = await apiClient.get("/admin/users/");
// //       setUsers(response.data);
// //     } catch (error) {
// //       console.error("Failed to fetch users:", error);
// //       const errorMessage =
// //         error.response?.data?.detail ||
// //         "An error occurred while fetching users.";
// //       showNotification(errorMessage, "error");
// //     } finally {
// //       setLoading(false);
// //     }
// //   }, [showNotification]);

// //   useEffect(() => {
// //     fetchUsers();
// //   }, [fetchUsers]);

// //   const handleAddUser = () => {
// //     setSelectedUser(null);
// //     setNewUser({
// //       username: "",
// //       email: "",
// //       password: "",
// //       user_role: "talent",
// //       phone_number: "",
// //     });
// //     setOpenDialog(true);
// //   };

// //   const handleEditUser = (user) => {
// //     setSelectedUser(user);
// //     setNewUser({
// //       username: user.username,
// //       email: user.email,
// //       user_role: user.user_role.toLowerCase(),
// //       phone_number: user.phone_number || "",
// //       password: "",
// //     });
// //     setOpenDialog(true);
// //   };

// //   const handleViewUser = (user) => {
// //     setViewingUser(user);
// //     setViewUserDialogOpen(true);
// //   };

// //   const handleDeleteClick = (user) => setDeleteConfirm({ open: true, user });
// //   const handleCloseDeleteConfirm = () =>
// //     setDeleteConfirm({ open: false, user: null });

// //   const handleDeleteConfirm = async () => {
// //     if (!deleteConfirm.user) return;
// //     try {
// //       await apiClient.delete(`/admin/users/${deleteConfirm.user.id}/`);
// //       showNotification(
// //         `User "${deleteConfirm.user.username}" deleted successfully!`,
// //         "success"
// //       );
// //       handleCloseDeleteConfirm();
// //       fetchUsers();
// //     } catch (error) {
// //       console.error("Failed to delete user:", error);
// //       showNotification(
// //         error.response?.data?.detail || "Failed to delete user.",
// //         "error"
// //       );
// //     }
// //   };

// //   const handleToggleUserStatus = async (user) => {
// //     if (!user) return;
// //     try {
// //       const newStatus = !user.is_active;
// //       await apiClient.patch(`/admin/users/${user.id}/`, {
// //         is_active: newStatus,
// //       });
// //       showNotification(
// //         `User "${user.username}" has been ${
// //           newStatus ? "activated" : "deactivated"
// //         }.`,
// //         "success"
// //       );
// //       fetchUsers();
// //     } catch (error) {
// //       console.error("Failed to update user status:", error);
// //       showNotification(
// //         error.response?.data?.detail || "Failed to update user status.",
// //         "error"
// //       );
// //     }
// //   };

// //   const handleSaveUser = async () => {
// //     try {
// //       const role = newUser.user_role.toUpperCase();
// //       if (selectedUser) {
// //         const payload = {
// //           username: newUser.username,
// //           email: newUser.email,
// //           user_role: role,
// //           phone_number: newUser.phone_number,
// //         };
// //         if (newUser.password) payload.password = newUser.password;
// //         await apiClient.patch(`/admin/users/${selectedUser.id}/`, payload);
// //         showNotification("User updated successfully!", "success");
// //       } else {
// //         if (!newUser.password) {
// //           showNotification("Password is required for new users.", "warning");
// //           return;
// //         }
// //         await apiClient.post("/admin/users/", { ...newUser, user_role: role });
// //         showNotification("User created successfully!", "success");
// //       }
// //       setOpenDialog(false);
// //       fetchUsers();
// //     } catch (error) {
// //       console.error("Failed to save user:", error);
// //       const errors = error.response?.data;
// //       let errorMessage = "An error occurred.";
// //       if (typeof errors === "object" && errors !== null) {
// //         errorMessage = Object.entries(errors)
// //           .map(
// //             ([key, value]) =>
// //               `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
// //           )
// //           .join(" | ");
// //       } else if (errors) {
// //         errorMessage = errors.toString();
// //       }
// //       showNotification(errorMessage, "error");
// //     }
// //   };

// //   const handleChangePage = (event, newPage) => setPage(newPage);
// //   const handleChangeRowsPerPage = (event) => {
// //     setRowsPerPage(parseInt(event.target.value, 10));
// //     setPage(0);
// //   };

// //   const filteredUsers = users.filter(
// //     (user) =>
// //       user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
// //       user.email.toLowerCase().includes(searchTerm.toLowerCase())
// //   );

// //   const userStats = {
// //     total: users.length,
// //     talent: users.filter((u) => u.user_role === "TALENT").length,
// //     employee: users.filter((u) => u.user_role === "EMPLOYER").length,
// //     admin: users.filter((u) => u.user_role === "ADMIN").length,
// //     active: users.filter((u) => u.is_active).length,
// //     pending: users.filter((u) => !u.is_active).length,
// //   };

// //   const sectionPaperStyles = {
// //     p: { xs: 2, md: 3 },
// //     borderRadius: "12px",
// //     backgroundColor: theme.palette.mint.main,
// //     boxShadow: theme.shadows[1],
// //   };

// //   return (
// //     <Layout title="User Management">
// //       <Box
// //         sx={{
// //           p: { xs: 2, md: 3 },
// //           backgroundColor: theme.palette.background.default,
// //         }}
// //       >
// //         <Stack spacing={3}>
// //           {/* ROW 1: HEADER */}
// //           <Paper
// //             elevation={0}
// //             sx={{
// //               ...sectionPaperStyles,
// //               display: "flex",
// //               flexDirection: { xs: "column", md: "row" },
// //               justifyContent: "space-between",
// //               alignItems: { xs: "flex-start", md: "center" },
// //             }}
// //           >
// //             <Box>
// //               <Typography
// //                 variant="h4"
// //                 sx={{ fontWeight: 700, color: theme.palette.primary.main }}
// //               >
// //                 User Management
// //               </Typography>
// //               <Typography variant="body1" color="text.secondary">
// //                 Manage all users across the GATEP Platform
// //               </Typography>
// //             </Box>
// //             <Button
// //               variant="contained"
// //               color="primary"
// //               startIcon={<Add />}
// //               onClick={handleAddUser}
// //               sx={{ mt: { xs: 2, md: 0 } }}
// //             >
// //               Add New User
// //             </Button>
// //           </Paper>

// //           {/* ROW 2: SEARCH & FILTERS */}
// //           <Paper
// //             elevation={0}
// //             sx={{
// //               p: 2,
// //               borderRadius: "12px",
// //               backgroundColor: "background.paper",
// //               border: `1px solid ${theme.palette.divider}`,
// //             }}
// //           >
// //             <Stack
// //               direction={{ xs: "column", md: "row" }}
// //               spacing={2}
// //               alignItems="center"
// //             >
// //               <TextField
// //                 fullWidth
// //                 placeholder="Search by username or email"
// //                 value={searchTerm}
// //                 onChange={(e) => setSearchTerm(e.target.value)}
// //                 InputProps={{
// //                   startAdornment: (
// //                     <InputAdornment position="start">
// //                       <Search color="disabled" />
// //                     </InputAdornment>
// //                   ),
// //                 }}
// //               />
// //             </Stack>
// //           </Paper>

// //           {/* ROW 3: STAT CARDS */}
// //           <Box
// //             sx={{
// //               display: "flex",
// //               gap: { xs: 1.5, md: 2 },
// //               flexWrap: { xs: "wrap", md: "nowrap" },
// //             }}
// //           >
// //             {(loading
// //               ? [...Array(6)]
// //               : [
// //                   { title: "Total Users", value: userStats.total },
// //                   { title: "AI Talent", value: userStats.talent },
// //                   { title: "Employers", value: userStats.employee },
// //                   { title: "Admins", value: userStats.admin },
// //                   { title: "Active", value: userStats.active },
// //                   { title: "Inactive", value: userStats.pending },
// //                 ]
// //             ).map((stat, index) => (
// //               <Box
// //                 key={index}
// //                 sx={{
// //                   flex: { xs: "1 1 45%", md: 1 },
// //                   minWidth: { xs: 150, md: 0 },
// //                 }}
// //               >
// //                 {loading ? (
// //                   <Skeleton
// //                     variant="rectangular"
// //                     height={120}
// //                     sx={{ borderRadius: "14px" }}
// //                   />
// //                 ) : (
// //                   <Card
// //                     sx={{
// //                       position: "relative",
// //                       backgroundColor: theme.palette.primary.main,
// //                       color: theme.palette.primary.contrastText,
// //                       height: 120,
// //                       borderRadius: "14px",
// //                       overflow: "hidden",
// //                       boxShadow: theme.shadows[2],
// //                     }}
// //                   >
// //                     <svg
// //                       width="100"
// //                       height="120"
// //                       viewBox="0 0 100 120"
// //                       style={{ position: "absolute", right: 0, top: 0 }}
// //                     >
// //                       <path
// //                         d="M40 0 L100 0 L100 120 C 80 120, 70 90, 70 60 C 70 30, 80 0, 40 0 Z"
// //                         fill={theme.palette.accent.main}
// //                       />
// //                     </svg>
// //                     <CardContent
// //                       sx={{
// //                         position: "relative",
// //                         zIndex: 1,
// //                         p: { xs: 1.5, md: 2 },
// //                         display: "flex",
// //                         flexDirection: "column",
// //                         justifyContent: "space-between",
// //                         height: "100%",
// //                       }}
// //                     >
// //                       <Typography
// //                         sx={{
// //                           fontWeight: 600,
// //                           fontSize: { xs: "0.75rem", md: "0.8rem" },
// //                           textTransform: "uppercase",
// //                           color: alpha(theme.palette.primary.contrastText, 0.8),
// //                         }}
// //                       >
// //                         {stat.title}
// //                       </Typography>
// //                       <Typography
// //                         sx={{
// //                           fontWeight: "bold",
// //                           fontSize: { xs: "2rem", md: "2.25rem" },
// //                           lineHeight: 1,
// //                         }}
// //                       >
// //                         {stat.value}
// //                       </Typography>
// //                     </CardContent>
// //                   </Card>
// //                 )}
// //               </Box>
// //             ))}
// //           </Box>

// //           {/* ROW 4: USERS TABLE */}
// //           <Paper
// //             elevation={0}
// //             sx={{
// //               borderRadius: "16px",
// //               overflow: "hidden",
// //               backgroundColor: "background.paper",
// //               border: `1px solid ${theme.palette.divider}`,
// //             }}
// //           >
// //             <TableContainer>
// //               <Table sx={{ minWidth: 800 }}>
// //                 <TableHead>
// //                   <TableRow>
// //                     {[
// //                       "User",
// //                       "Role",
// //                       "Status",
// //                       "Join Date",
// //                       "Last Login",
// //                       "Actions",
// //                     ].map((headCell) => (
// //                       <TableCell key={headCell}>{headCell}</TableCell>
// //                     ))}
// //                   </TableRow>
// //                 </TableHead>
// //                 <TableBody>
// //                   {loading
// //                     ? Array.from(new Array(rowsPerPage)).map((_, index) => (
// //                         <TableRow key={index}>
// //                           <TableCell>
// //                             <Stack
// //                               direction="row"
// //                               alignItems="center"
// //                               spacing={2}
// //                             >
// //                               <Skeleton
// //                                 variant="circular"
// //                                 width={40}
// //                                 height={40}
// //                               />
// //                               <Box>
// //                                 <Skeleton width={80} />
// //                                 <Skeleton width={120} />
// //                               </Box>
// //                             </Stack>
// //                           </TableCell>
// //                           <TableCell>
// //                             <Skeleton width={80} height={24} />
// //                           </TableCell>
// //                           <TableCell>
// //                             <Skeleton width={70} height={24} />
// //                           </TableCell>
// //                           <TableCell>
// //                             <Skeleton width={90} />
// //                           </TableCell>
// //                           <TableCell>
// //                             <Skeleton width={90} />
// //                           </TableCell>
// //                           <TableCell>
// //                             <Stack direction="row">
// //                               <Skeleton
// //                                 variant="circular"
// //                                 width={32}
// //                                 height={32}
// //                               />
// //                               <Skeleton
// //                                 variant="circular"
// //                                 width={32}
// //                                 height={32}
// //                               />
// //                               <Skeleton
// //                                 variant="circular"
// //                                 width={32}
// //                                 height={32}
// //                               />
// //                             </Stack>
// //                           </TableCell>
// //                         </TableRow>
// //                       ))
// //                     : filteredUsers
// //                         .slice(
// //                           page * rowsPerPage,
// //                           page * rowsPerPage + rowsPerPage
// //                         )
// //                         .map((user) => (
// //                           <TableRow key={user.id} hover>
// //                             <TableCell>
// //                               <Stack
// //                                 direction="row"
// //                                 alignItems="center"
// //                                 spacing={2}
// //                                 sx={{ cursor: "pointer" }}
// //                                 onClick={() => handleViewUser(user)}
// //                               >
// //                                 <Avatar
// //                                   sx={{
// //                                     bgcolor: getRoleChipStyle(user.user_role)
// //                                       .color,
// //                                   }}
// //                                 >
// //                                   {user.username.charAt(0).toUpperCase()}
// //                                 </Avatar>
// //                                 <Box>
// //                                   <Typography variant="body2" fontWeight={600}>
// //                                     {user.username}
// //                                   </Typography>
// //                                   <Typography
// //                                     variant="caption"
// //                                     color="text.secondary"
// //                                   >
// //                                     {user.email}
// //                                   </Typography>
// //                                 </Box>
// //                               </Stack>
// //                             </TableCell>
// //                             <TableCell>
// //                               <Chip
// //                                 label={user.user_role}
// //                                 size="small"
// //                                 sx={{
// //                                   ...getRoleChipStyle(user.user_role),
// //                                   textTransform: "capitalize",
// //                                 }}
// //                               />
// //                             </TableCell>
// //                             <TableCell>
// //                               <Chip
// //                                 label={user.is_active ? "Active" : "Inactive"}
// //                                 size="small"
// //                                 color={user.is_active ? "success" : "warning"}
// //                                 variant="outlined"
// //                               />
// //                             </TableCell>
// //                             <TableCell>
// //                               {formatDate(user.date_joined)}
// //                             </TableCell>
// //                             <TableCell>{formatDate(user.last_login)}</TableCell>
// //                             <TableCell>
// //                               <Stack direction="row" spacing={0}>
// //                                 <Tooltip
// //                                   title={
// //                                     user.is_active
// //                                       ? "Deactivate User"
// //                                       : "Activate User"
// //                                   }
// //                                 >
// //                                   <IconButton
// //                                     size="small"
// //                                     onClick={() => handleToggleUserStatus(user)}
// //                                     color={
// //                                       user.is_active ? "warning" : "success"
// //                                     }
// //                                   >
// //                                     {user.is_active ? (
// //                                       <ToggleOff />
// //                                     ) : (
// //                                       <ToggleOn />
// //                                     )}
// //                                   </IconButton>
// //                                 </Tooltip>
// //                                 <Tooltip title="Edit User">
// //                                   <IconButton
// //                                     size="small"
// //                                     onClick={() => handleEditUser(user)}
// //                                   >
// //                                     <Edit fontSize="small" color="primary" />
// //                                   </IconButton>
// //                                 </Tooltip>
// //                                 <Tooltip title="Delete User">
// //                                   <IconButton
// //                                     size="small"
// //                                     onClick={() => handleDeleteClick(user)}
// //                                   >
// //                                     <Delete fontSize="small" color="error" />
// //                                   </IconButton>
// //                                 </Tooltip>
// //                               </Stack>
// //                             </TableCell>
// //                           </TableRow>
// //                         ))}
// //                   {!loading && filteredUsers.length === 0 && (
// //                     <TableRow>
// //                       <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
// //                         <Typography>No users found.</Typography>
// //                       </TableCell>
// //                     </TableRow>
// //                   )}
// //                 </TableBody>
// //               </Table>
// //             </TableContainer>
// //             <TablePagination
// //               rowsPerPageOptions={[5, 10, 25, 50]}
// //               component="div"
// //               count={filteredUsers.length}
// //               rowsPerPage={rowsPerPage}
// //               page={page}
// //               onPageChange={handleChangePage}
// //               onRowsPerPageChange={handleChangeRowsPerPage}
// //             />
// //           </Paper>
// //         </Stack>
// //       </Box>

// //       {/* DIALOGS */}
// //       <Dialog
// //         open={openDialog}
// //         onClose={() => setOpenDialog(false)}
// //         maxWidth="sm"
// //         fullWidth
// //         fullScreen={isMobile}
// //       >
// //         <DialogTitle>{selectedUser ? "Edit User" : "Add New User"}</DialogTitle>
// //         <DialogContent>
// //           <Grid container spacing={2} sx={{ mt: 1 }}>
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Username"
// //                 value={newUser.username}
// //                 onChange={(e) =>
// //                   setNewUser((p) => ({ ...p, username: e.target.value }))
// //                 }
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Email Address"
// //                 type="email"
// //                 value={newUser.email}
// //                 onChange={(e) =>
// //                   setNewUser((p) => ({ ...p, email: e.target.value }))
// //                 }
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Phone Number"
// //                 value={newUser.phone_number}
// //                 onChange={(e) =>
// //                   setNewUser((p) => ({ ...p, phone_number: e.target.value }))
// //                 }
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Password"
// //                 type="password"
// //                 helperText={
// //                   selectedUser
// //                     ? "Leave blank to keep current password"
// //                     : "Required for new users"
// //                 }
// //                 value={newUser.password}
// //                 onChange={(e) =>
// //                   setNewUser((p) => ({ ...p, password: e.target.value }))
// //                 }
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <FormControl fullWidth>
// //                 <InputLabel>Role</InputLabel>
// //                 <Select
// //                   value={newUser.user_role}
// //                   onChange={(e) =>
// //                     setNewUser((p) => ({ ...p, user_role: e.target.value }))
// //                   }
// //                   label="Role"
// //                 >
// //                   <MenuItem value="talent">Talent</MenuItem>
// //                   <MenuItem value="employer">Employer</MenuItem>
// //                   <MenuItem value="admin">Admin</MenuItem>
// //                 </Select>
// //               </FormControl>
// //             </Grid>
// //           </Grid>
// //         </DialogContent>
// //         <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
// //           <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
// //           <Button variant="contained" color="primary" onClick={handleSaveUser}>
// //             {selectedUser ? "Update User" : "Create User"}
// //           </Button>
// //         </DialogActions>
// //       </Dialog>
// //       <Dialog
// //         open={deleteConfirm.open}
// //         onClose={handleCloseDeleteConfirm}
// //         maxWidth="xs"
// //         fullWidth
// //       >
// //         <DialogTitle>Confirm Deletion</DialogTitle>
// //         <DialogContent>
// //           <Typography>
// //             Are you sure you want to delete the user "
// //             {deleteConfirm.user?.username}"? This action cannot be undone.
// //           </Typography>
// //         </DialogContent>
// //         <DialogActions>
// //           <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
// //           <Button
// //             variant="contained"
// //             color="error"
// //             onClick={handleDeleteConfirm}
// //           >
// //             Delete
// //           </Button>
// //         </DialogActions>
// //       </Dialog>
// //       {viewingUser && (
// //         <Dialog
// //           open={viewUserDialogOpen}
// //           onClose={() => setViewUserDialogOpen(false)}
// //           maxWidth="md"
// //           fullWidth
// //           fullScreen={isMobile}
// //         >
// //           <DialogTitle sx={{ p: 2, bgcolor: "background.paper" }}>
// //             <Stack
// //               direction="row"
// //               justifyContent="space-between"
// //               alignItems="center"
// //             >
// //               <Typography variant="h6" fontWeight={600}>
// //                 User Details
// //               </Typography>
// //               <IconButton onClick={() => setViewUserDialogOpen(false)}>
// //                 <Close />
// //               </IconButton>
// //             </Stack>
// //           </DialogTitle>
// //           <Divider />
// //           <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "action.hover" }}>
// //             <Grid container spacing={3}>
// //               <Grid item xs={12} md={4}>
// //                 <Paper
// //                   elevation={0}
// //                   sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}
// //                 >
// //                   <Avatar
// //                     sx={{
// //                       width: 100,
// //                       height: 100,
// //                       fontSize: "3rem",
// //                       mx: "auto",
// //                       mb: 2,
// //                       bgcolor: getRoleChipStyle(viewingUser.user_role).color,
// //                     }}
// //                   >
// //                     {viewingUser.username.charAt(0).toUpperCase()}
// //                   </Avatar>
// //                   <Typography variant="h5" fontWeight={700}>
// //                     {viewingUser.username}
// //                   </Typography>
// //                   <Typography color="text.secondary" gutterBottom>
// //                     {viewingUser.email}
// //                   </Typography>
// //                   <Chip
// //                     label={viewingUser.user_role}
// //                     size="small"
// //                     sx={{
// //                       ...getRoleChipStyle(viewingUser.user_role),
// //                       fontWeight: 600,
// //                       borderRadius: "6px",
// //                       textTransform: "capitalize",
// //                       mt: 1,
// //                     }}
// //                   />
// //                 </Paper>
// //               </Grid>
// //               <Grid item xs={12} md={8}>
// //                 <Stack spacing={3}>
// //                   <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
// //                     <Typography variant="h6" fontWeight={600} mb={2}>
// //                       Account Information
// //                     </Typography>
// //                     <List dense>
// //                       <DetailItem
// //                         icon={<Badge />}
// //                         primary="User ID"
// //                         secondary={viewingUser.id}
// //                       />
// //                       <DetailItem
// //                         icon={<Phone />}
// //                         primary="Phone Number"
// //                         secondary={viewingUser.phone_number}
// //                       />
// //                       <DetailItem
// //                         icon={
// //                           viewingUser.is_active ? (
// //                             <TaskAlt color="success" />
// //                           ) : (
// //                             <PendingActions color="warning" />
// //                           )
// //                         }
// //                         primary="Status"
// //                         secondary={
// //                           viewingUser.is_active
// //                             ? "Active"
// //                             : "Inactive / Pending"
// //                         }
// //                       />
// //                       <DetailItem
// //                         icon={<CalendarToday />}
// //                         primary="Date Joined"
// //                         secondary={formatDate(viewingUser.date_joined)}
// //                       />
// //                       <DetailItem
// //                         icon={<Login />}
// //                         primary="Last Login"
// //                         secondary={formatDate(viewingUser.last_login)}
// //                       />
// //                     </List>
// //                   </Paper>
// //                   <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
// //                     <Typography variant="h6" fontWeight={600} mb={2}>
// //                       Roles & Permissions
// //                     </Typography>
// //                     <List dense>
// //                       <DetailItem
// //                         icon={
// //                           viewingUser.is_staff ? (
// //                             <TaskAlt color="success" />
// //                           ) : (
// //                             <PendingActions color="error" />
// //                           )
// //                         }
// //                         primary="Staff Access (Admin)"
// //                         secondary={viewingUser.is_staff ? "Yes" : "No"}
// //                       />
// //                       <ListItem disablePadding>
// //                         <ListItemIcon sx={{ minWidth: 40 }}>
// //                           <VerifiedUser />
// //                         </ListItemIcon>
// //                         <ListItemText
// //                           primary={
// //                             <Typography variant="body2" color="text.secondary">
// //                               User Permissions
// //                             </Typography>
// //                           }
// //                           secondary={
// //                             viewingUser.user_permissions.length > 0 ? (
// //                               <Stack
// //                                 direction="row"
// //                                 spacing={1}
// //                                 flexWrap="wrap"
// //                                 mt={0.5}
// //                               >
// //                                 {viewingUser.user_permissions.map((p) => (
// //                                   <Chip key={p} label={p} size="small" />
// //                                 ))}
// //                               </Stack>
// //                             ) : (
// //                               "None"
// //                             )
// //                           }
// //                         />
// //                       </ListItem>
// //                     </List>
// //                   </Paper>
// //                 </Stack>
// //               </Grid>
// //             </Grid>
// //           </DialogContent>
// //         </Dialog>
// //       )}
// //     </Layout>
// //   );
// // };

// // export default UserManagement;

// // "use client";

// // import { useState, useEffect, useCallback } from "react";
// // import {
// //   Grid,
// //   Card,
// //   CardContent,
// //   Container,
// //   Typography,
// //   Button,
// //   Box,
// //   Table,
// //   TableBody,
// //   TableCell,
// //   TableContainer,
// //   TableHead,
// //   TableRow,
// //   Avatar,
// //   IconButton,
// //   Dialog,
// //   DialogTitle,
// //   DialogContent,
// //   DialogActions,
// //   TextField,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   MenuItem,
// //   Chip,
// //   CircularProgress,
// //   Stack,
// //   Paper,
// //   useTheme,
// //   useMediaQuery,
// //   TablePagination,
// //   Divider,
// //   ListItem,
// //   ListItemText,
// //   ListItemIcon,
// //   List,
// //   InputAdornment,
// //   Skeleton, // Import Skeleton
// // } from "@mui/material";
// // import {
// //   Add,
// //   Edit,
// //   Delete,
// //   Search,
// //   Phone,
// //   Close,
// //   Badge,
// //   CalendarToday,
// //   Login,
// //   CheckCircle,
// //   Cancel,
// //   VerifiedUser,
// //   KeyboardArrowDown,
// //   TaskAlt,
// //   PendingActions,
// // } from "@mui/icons-material";
// // import Layout from "../common/Layout";
// // import { useNotification } from "../../context/NotificationContext";
// // import apiClient from "../../context/apiClient";

// // const DetailItem = ({ icon, primary, secondary }) => (
// //   <ListItem disablePadding>
// //     <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
// //       {icon}
// //     </ListItemIcon>
// //     <ListItemText
// //       primary={
// //         <Typography variant="body2" color="text.secondary">
// //           {primary}
// //         </Typography>
// //       }
// //       secondary={
// //         <Typography variant="body1" fontWeight={500} color="text.primary">
// //           {secondary || "N/A"}
// //         </Typography>
// //       }
// //     />
// //   </ListItem>
// // );

// // const UserManagement = () => {
// //   const { showNotification } = useNotification();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

// //   const [users, setUsers] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [openDialog, setOpenDialog] = useState(false);
// //   const [deleteConfirm, setDeleteConfirm] = useState({
// //     open: false,
// //     user: null,
// //   });
// //   const [selectedUser, setSelectedUser] = useState(null);
// //   const [searchTerm, setSearchTerm] = useState("");
// //   const [newUser, setNewUser] = useState({
// //     username: "",
// //     email: "",
// //     password: "",
// //     user_role: "talent",
// //     phone_number: "",
// //   });

// //   const [page, setPage] = useState(0);
// //   const [rowsPerPage, setRowsPerPage] = useState(5);

// //   const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
// //   const [viewingUser, setViewingUser] = useState(null);

// //   const getRoleChipStyle = (role) => {
// //     switch (role?.toLowerCase()) {
// //       case "talent":
// //         return {
// //           bgcolor: "rgba(40, 167, 69, 0.1)",
// //           color: "#28a745",
// //         };
// //       case "employee":
// //       case "employer":
// //         return {
// //           bgcolor: "rgba(255, 193, 7, 0.1)",
// //           color: "#ffc107",
// //         };
// //       case "admin":
// //         return {
// //           bgcolor: "rgba(220, 53, 69, 0.1)",
// //           color: "#dc3545",
// //         };
// //       default:
// //         return {
// //           bgcolor: "rgba(108, 117, 125, 0.1)",
// //           color: "#6c757d",
// //         };
// //     }
// //   };

// //   const formatDate = (isoString) => {
// //     if (!isoString) return "N/A";
// //     const date = new Date(isoString);
// //     const day = String(date.getDate()).padStart(2, "0");
// //     const month = String(date.getMonth() + 1).padStart(2, "0");
// //     const year = date.getFullYear();
// //     return `${day}-${month}-${year}`;
// //   };

// //   const fetchUsers = useCallback(async () => {
// //     setLoading(true);
// //     try {
// //       // Simulate network delay for skeleton visibility
// //       await new Promise((resolve) => setTimeout(resolve, 1500));
// //       const response = await apiClient.get("/admin/users/");
// //       setUsers(response.data);
// //     } catch (error) {
// //       console.error("Failed to fetch users:", error);
// //       const errorMessage =
// //         error.response?.data?.detail ||
// //         "An error occurred while fetching users.";
// //       showNotification(errorMessage, "error");
// //     } finally {
// //       setLoading(false);
// //     }
// //   }, [showNotification]);

// //   useEffect(() => {
// //     fetchUsers();
// //   }, [fetchUsers]);

// //   const handleAddUser = () => {
// //     setSelectedUser(null);
// //     setNewUser({
// //       username: "",
// //       email: "",
// //       password: "",
// //       user_role: "talent",
// //       phone_number: "",
// //     });
// //     setOpenDialog(true);
// //   };

// //   const handleEditUser = (user) => {
// //     setSelectedUser(user);
// //     setNewUser({
// //       username: user.username,
// //       email: user.email,
// //       user_role: user.user_role.toLowerCase(),
// //       phone_number: user.phone_number || "",
// //       password: "",
// //     });
// //     setOpenDialog(true);
// //   };

// //   const handleViewUser = (user) => {
// //     setViewingUser(user);
// //     setViewUserDialogOpen(true);
// //   };

// //   const handleDeleteClick = (user) => {
// //     setDeleteConfirm({ open: true, user });
// //   };

// //   const handleCloseDeleteConfirm = () => {
// //     setDeleteConfirm({ open: false, user: null });
// //   };

// //   const handleDeleteConfirm = async () => {
// //     if (!deleteConfirm.user) return;
// //     try {
// //       await apiClient.delete(`/admin/users/${deleteConfirm.user.id}/`);
// //       showNotification(
// //         `User "${deleteConfirm.user.username}" deleted successfully!`,
// //         "success"
// //       );
// //       handleCloseDeleteConfirm();
// //       fetchUsers();
// //     } catch (error) {
// //       console.error("Failed to delete user:", error);
// //       const errorMessage =
// //         error.response?.data?.detail || "Failed to delete user.";
// //       showNotification(errorMessage, "error");
// //     }
// //   };

// //   const handleSaveUser = async () => {
// //     try {
// //       const role = newUser.user_role.toUpperCase();
// //       if (selectedUser) {
// //         const payload = {
// //           username: newUser.username,
// //           email: newUser.email,
// //           user_role: role,
// //           phone_number: newUser.phone_number,
// //         };
// //         if (newUser.password) {
// //           payload.password = newUser.password;
// //         }
// //         await apiClient.patch(`/admin/users/${selectedUser.id}/`, payload);
// //         showNotification("User updated successfully!", "success");
// //       } else {
// //         if (!newUser.password) {
// //           showNotification("Password is required for new users.", "warning");
// //           return;
// //         }
// //         const payload = { ...newUser, user_role: role };
// //         await apiClient.post("/admin/users/", payload);
// //         showNotification("User created successfully!", "success");
// //       }
// //       setOpenDialog(false);
// //       fetchUsers();
// //     } catch (error) {
// //       console.error("Failed to save user:", error);
// //       const errors = error.response?.data;
// //       let errorMessage = "An error occurred while saving the user.";
// //       if (typeof errors === "object" && errors !== null) {
// //         errorMessage = Object.entries(errors)
// //           .map(
// //             ([key, value]) =>
// //               `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
// //           )
// //           .join(" | ");
// //       } else if (errors) {
// //         errorMessage = errors.toString();
// //       }
// //       showNotification(errorMessage, "error");
// //     }
// //   };

// //   const handleChangePage = (event, newPage) => {
// //     setPage(newPage);
// //   };

// //   const handleChangeRowsPerPage = (event) => {
// //     setRowsPerPage(Number.parseInt(event.target.value, 10));
// //     setPage(0);
// //   };

// //   const filteredUsers = users.filter(
// //     (user) =>
// //       user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
// //       user.email.toLowerCase().includes(searchTerm.toLowerCase())
// //   );

// //   const userStats = {
// //     total: users.length,
// //     talent: users.filter((u) => u.user_role === "TALENT").length,
// //     employee: users.filter((u) => u.user_role === "EMPLOYER").length,
// //     admin: users.filter((u) => u.user_role === "ADMIN").length,
// //     active: users.filter((u) => u.is_active).length,
// //     pending: users.filter((u) => !u.is_active).length,
// //   };

// //   const StatCard = ({ title, value }) => (
// //     <Card
// //       elevation={0}
// //       sx={{
// //         position: "relative",
// //         backgroundColor: "#5A36CC",
// //         minWidth: "195px",
// //         width: "150px",
// //         height: "120px",
// //         borderRadius: "14px",
// //         overflow: "hidden",
// //         boxShadow: "0 4px 12px rgba(90, 54, 204, 0.25)",
// //         flexShrink: 0,
// //       }}
// //     >
// //       <Box
// //         sx={{
// //           position: "absolute",
// //           right: 0,
// //           top: 0,
// //           width: "100%",
// //           height: "100%",
// //           zIndex: 1,
// //         }}
// //       >
// //         <svg
// //           width="100%"
// //           height="100%"
// //           viewBox="0 0 280 140"
// //           preserveAspectRatio="none"
// //           style={{ position: "absolute", right: 0, top: 0 }}
// //         >
// //           <path
// //             d="M200 0 L280 0 L280 140 C 250 140, 230 105, 230 70 C 230 35, 250 0, 200 0 Z"
// //             fill="#F9B64C"
// //           />
// //         </svg>
// //       </Box>
// //       <CardContent
// //         sx={{
// //           position: "relative",
// //           zIndex: 2,
// //           p: 3,
// //           height: "100%",
// //           display: "flex",
// //           flexDirection: "column",
// //           justifyContent: "flex-start",
// //         }}
// //       >
// //         <Typography
// //           variant="subtitle2"
// //           sx={{
// //             fontWeight: 500,
// //             textTransform: "uppercase",
// //             color: "#C3C3C3",
// //             fontSize: "15px",
// //             mb: 1,
// //           }}
// //         >
// //           {title}
// //         </Typography>
// //         <Typography
// //           variant="h4"
// //           sx={{
// //             fontWeight: 700,
// //             color: "#FFFFFF",
// //             fontSize: "30px",
// //             lineHeight: 1,
// //           }}
// //         >
// //           {value}
// //         </Typography>
// //       </CardContent>
// //     </Card>
// //   );

// //   const TableSkeleton = () => (
// //     <TableContainer>
// //       <Table sx={{ minWidth: 800 }}>
// //         <TableHead sx={{ bgcolor: "#F1FFFA" }}>
// //           <TableRow>
// //             {[
// //               "User",
// //               "Role",
// //               "Status",
// //               "Join Date",
// //               "Last Login",
// //               "Actions",
// //             ].map((headCell) => (
// //               <TableCell
// //                 key={headCell}
// //                 sx={{
// //                   fontWeight: "bold",
// //                   color: "#A9A5A5",
// //                   borderBottom: "none",
// //                   textTransform: "uppercase",
// //                   fontSize: "0.75rem",
// //                 }}
// //               >
// //                 {headCell}
// //               </TableCell>
// //             ))}
// //           </TableRow>
// //         </TableHead>
// //         <TableBody>
// //           {Array.from(new Array(rowsPerPage)).map((_, index) => (
// //             <TableRow
// //               key={index}
// //               sx={{ "&:hover": { backgroundColor: "#F1FFFA" } }}
// //             >
// //               <TableCell sx={{ borderBottomColor: "#f5f5f5" }}>
// //                 <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
// //                   <Skeleton variant="circular" width={40} height={40} />
// //                   <Box>
// //                     <Skeleton
// //                       variant="text"
// //                       sx={{ fontSize: "1rem" }}
// //                       width={100}
// //                     />
// //                     <Skeleton
// //                       variant="text"
// //                       sx={{ fontSize: "0.75rem" }}
// //                       width={140}
// //                     />
// //                   </Box>
// //                 </Box>
// //               </TableCell>
// //               <TableCell sx={{ borderBottomColor: "#f5f5f5" }}>
// //                 <Skeleton
// //                   variant="rectangular"
// //                   width={80}
// //                   height={24}
// //                   sx={{ borderRadius: "6px" }}
// //                 />
// //               </TableCell>
// //               <TableCell sx={{ borderBottomColor: "#f5f5f5" }}>
// //                 <Skeleton
// //                   variant="rectangular"
// //                   width={80}
// //                   height={24}
// //                   sx={{ borderRadius: "6px" }}
// //                 />
// //               </TableCell>
// //               <TableCell sx={{ borderBottomColor: "#f5f5f5" }}>
// //                 <Skeleton variant="text" width={90} />
// //               </TableCell>
// //               <TableCell sx={{ borderBottomColor: "#f5f5f5" }}>
// //                 <Skeleton variant="text" width={90} />
// //               </TableCell>
// //               <TableCell sx={{ borderBottomColor: "#f5f5f5" }}>
// //                 <Stack direction="row" spacing={0}>
// //                   <Skeleton variant="circular" width={32} height={32} />
// //                   <Skeleton variant="circular" width={32} height={32} />
// //                 </Stack>
// //               </TableCell>
// //             </TableRow>
// //           ))}
// //         </TableBody>
// //       </Table>
// //     </TableContainer>
// //   );

// //   return (
// //     <Layout title="User Management">
// //       <Box sx={{ minHeight: "100vh" }}>
// //         <Container
// //           maxWidth={false}
// //           sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 4 } }}
// //         >
// //           <Box
// //             sx={{
// //               display: "flex",
// //               flexDirection: { xs: "column", md: "row" },
// //               justifyContent: "space-between",
// //               alignItems: { xs: "flex-start", md: "center" },
// //               gap: 2,
// //               mb: 4,
// //               backgroundColor: "#F1FFFA",
// //               boxShadow: "0 4px 12px 0 rgba(0,0,0,0.08)",
// //               borderRadius: "12px",
// //               p: 3,
// //             }}
// //           >
// //             <Box>
// //               <Typography
// //                 variant="h4"
// //                 sx={{ fontWeight: 700, color: "#5A36CC" }}
// //               >
// //                 User Managment
// //               </Typography>
// //               <Typography variant="body1" color="#A9A5A5">
// //                 Manage talent employee and admin user across the GATEP Platform
// //               </Typography>
// //             </Box>
// //             <Button
// //               variant="outlined"
// //               startIcon={<Add />}
// //               onClick={handleAddUser}
// //               sx={{
// //                 borderRadius: "8px",
// //                 textTransform: "none",
// //                 fontWeight: 600,
// //                 borderColor: "#4F46BA",
// //                 color: "#4F46BA",
// //                 "&:hover": {
// //                   backgroundColor: "rgba(79, 70, 186, 0.04)",
// //                   borderColor: "#4F46BA",
// //                 },
// //                 px: 2,
// //                 py: 1,
// //               }}
// //             >
// //               Add New User
// //             </Button>
// //           </Box>

// //           <Paper
// //             elevation={0}
// //             sx={{
// //               p: 2,
// //               mb: 4,
// //               borderRadius: "12px",
// //               backgroundColor: "white",
// //               border: "1px solid #e8e8e8",
// //             }}
// //           >
// //             <Stack
// //               direction={{ xs: "column", md: "row" }}
// //               spacing={2}
// //               alignItems="center"
// //             >
// //               <TextField
// //                 fullWidth
// //                 placeholder="Search users"
// //                 value={searchTerm}
// //                 onChange={(e) => setSearchTerm(e.target.value)}
// //                 variant="outlined"
// //                 InputProps={{
// //                   startAdornment: (
// //                     <InputAdornment position="start">
// //                       <Search sx={{ color: "#A9A5A5" }} />
// //                     </InputAdornment>
// //                   ),
// //                   sx: {
// //                     borderRadius: "8px",
// //                     backgroundColor: "#F1FFFA",
// //                     "& .MuiOutlinedInput-notchedOutline": {
// //                       border: "none",
// //                     },
// //                   },
// //                 }}
// //               />
// //               <FormControl sx={{ width: { xs: "100%", md: 200 } }}>
// //                 <Select
// //                   defaultValue="last_month"
// //                   IconComponent={KeyboardArrowDown}
// //                   sx={{
// //                     borderRadius: "8px",
// //                     backgroundColor: "#F1FFFA",
// //                     "& .MuiOutlinedInput-notchedOutline": { border: "none" },
// //                   }}
// //                 >
// //                   <MenuItem value="last_month">Last Month</MenuItem>
// //                   <MenuItem value="last_3_months">Last 3 Months</MenuItem>
// //                 </Select>
// //               </FormControl>
// //               <FormControl sx={{ width: { xs: "100%", md: 200 } }}>
// //                 <Select
// //                   defaultValue="all_regions"
// //                   IconComponent={KeyboardArrowDown}
// //                   sx={{
// //                     borderRadius: "8px",
// //                     backgroundColor: "#F1FFFA",
// //                     "& .MuiOutlinedInput-notchedOutline": { border: "none" },
// //                   }}
// //                 >
// //                   <MenuItem value="all_regions">All Regions</MenuItem>
// //                   <MenuItem value="jakarta">Jakarta</MenuItem>
// //                 </Select>
// //               </FormControl>
// //             </Stack>
// //           </Paper>

// //           <Grid container spacing={0.5} sx={{ mb: 4 }}>
// //             {loading
// //               ? Array.from(new Array(6)).map((_, index) => (
// //                   <Grid item xs={12} sm={6} md={4} key={index}>
// //                     <Skeleton
// //                       variant="rectangular"
// //                       height={120}
// //                       sx={{ borderRadius: "14px" }}
// //                     />
// //                   </Grid>
// //                 ))
// //               : [
// //                   { title: "Total User", value: userStats.total },
// //                   { title: "AI Talent", value: userStats.talent },
// //                   { title: "Employee", value: userStats.employee },
// //                   { title: "Admin", value: userStats.admin },
// //                   { title: "Active", value: userStats.active },
// //                   { title: "Inactive", value: userStats.pending },
// //                 ].map((stat) => (
// //                   <Grid item xs={12} sm={6} md={4} key={stat.title}>
// //                     <StatCard title={stat.title} value={stat.value} />
// //                   </Grid>
// //                 ))}
// //           </Grid>

// //           <Paper
// //             elevation={0}
// //             sx={{
// //               borderRadius: "16px",
// //               overflow: "hidden",
// //               backgroundColor: "white",
// //               border: "1px solid #e8e8e8",
// //             }}
// //           >
// //             {loading ? (
// //               <TableSkeleton />
// //             ) : (
// //               <>
// //                 <TableContainer>
// //                   <Table sx={{ minWidth: 800 }}>
// //                     <TableHead sx={{ bgcolor: "#F1FFFA" }}>
// //                       <TableRow>
// //                         {[
// //                           "Region",
// //                           "Role",
// //                           "Status",
// //                           "Join Date",
// //                           "Last Login",
// //                           "Actions",
// //                         ].map((headCell) => (
// //                           <TableCell
// //                             key={headCell}
// //                             sx={{
// //                               fontWeight: "bold",
// //                               color: "#A9A5A5",
// //                               borderBottom: "none",
// //                               textTransform: "uppercase",
// //                               fontSize: "0.75rem",
// //                             }}
// //                           >
// //                             {headCell}
// //                           </TableCell>
// //                         ))}
// //                       </TableRow>
// //                     </TableHead>
// //                     <TableBody>
// //                       {filteredUsers.length > 0 ? (
// //                         filteredUsers
// //                           .slice(
// //                             page * rowsPerPage,
// //                             page * rowsPerPage + rowsPerPage
// //                           )
// //                           .map((user) => {
// //                             const roleStyle = getRoleChipStyle(user.user_role);
// //                             return (
// //                               <TableRow
// //                                 key={user.id}
// //                                 sx={{
// //                                   "&:last-child td, &:last-child th": {
// //                                     border: 0,
// //                                   },
// //                                   "&:hover": { backgroundColor: "#F1FFFA" },
// //                                 }}
// //                               >
// //                                 <TableCell
// //                                   sx={{ borderBottomColor: "#f5f5f5" }}
// //                                 >
// //                                   <Box
// //                                     sx={{
// //                                       display: "flex",
// //                                       alignItems: "center",
// //                                       gap: 2,
// //                                     }}
// //                                   >
// //                                     <Avatar
// //                                       onClick={() => handleViewUser(user)}
// //                                       sx={{
// //                                         bgcolor: roleStyle.color,
// //                                         color: "white",
// //                                         cursor: "pointer",
// //                                       }}
// //                                     >
// //                                       {user.username.charAt(0).toUpperCase()}
// //                                     </Avatar>
// //                                     <Box>
// //                                       <Typography
// //                                         variant="body2"
// //                                         fontWeight={600}
// //                                         color="#000000"
// //                                       >
// //                                         {user.username}
// //                                       </Typography>
// //                                       <Typography
// //                                         variant="caption"
// //                                         color="#A9A5A5"
// //                                       >
// //                                         {user.email}
// //                                       </Typography>
// //                                     </Box>
// //                                   </Box>
// //                                 </TableCell>
// //                                 <TableCell
// //                                   sx={{ borderBottomColor: "#f5f5f5" }}
// //                                 >
// //                                   <Chip
// //                                     label={user.user_role}
// //                                     sx={{
// //                                       ...roleStyle,
// //                                       fontWeight: 500,
// //                                       borderRadius: "6px",
// //                                       textTransform: "capitalize",
// //                                     }}
// //                                   />
// //                                 </TableCell>
// //                                 <TableCell
// //                                   sx={{ borderBottomColor: "#f5f5f5" }}
// //                                 >
// //                                   <Chip
// //                                     label={
// //                                       user.is_active ? "Active" : "Inactive"
// //                                     }
// //                                     sx={{
// //                                       bgcolor: user.is_active
// //                                         ? "rgba(40, 167, 69, 0.1)"
// //                                         : "rgba(255, 193, 7, 0.1)",
// //                                       color: user.is_active
// //                                         ? "#28a745"
// //                                         : "#ffc107",
// //                                       fontWeight: 500,
// //                                       borderRadius: "6px",
// //                                       textTransform: "capitalize",
// //                                     }}
// //                                   />
// //                                 </TableCell>
// //                                 <TableCell
// //                                   sx={{
// //                                     color: "#000000",
// //                                     borderBottomColor: "#f5f5f5",
// //                                   }}
// //                                 >
// //                                   {formatDate(user.date_joined)}
// //                                 </TableCell>
// //                                 <TableCell
// //                                   sx={{
// //                                     color: "#000000",
// //                                     borderBottomColor: "#f5f5f5",
// //                                   }}
// //                                 >
// //                                   {formatDate(user.last_login)}
// //                                 </TableCell>
// //                                 <TableCell
// //                                   sx={{ borderBottomColor: "#f5f5f5" }}
// //                                 >
// //                                   <Stack direction="row" spacing={0}>
// //                                     <IconButton
// //                                       size="small"
// //                                       onClick={() => handleEditUser(user)}
// //                                     >
// //                                       <Edit
// //                                         fontSize="small"
// //                                         sx={{ color: "#4F46BA" }}
// //                                       />
// //                                     </IconButton>
// //                                     <IconButton
// //                                       size="small"
// //                                       onClick={() => handleDeleteClick(user)}
// //                                     >
// //                                       <Delete
// //                                         fontSize="small"
// //                                         sx={{ color: "#A9A5A5" }}
// //                                       />
// //                                     </IconButton>
// //                                   </Stack>
// //                                 </TableCell>
// //                               </TableRow>
// //                             );
// //                           })
// //                       ) : (
// //                         <TableRow>
// //                           <TableCell
// //                             colSpan={6}
// //                             align="center"
// //                             sx={{ py: 5, borderBottom: "none" }}
// //                           >
// //                             <Typography>No users found.</Typography>
// //                           </TableCell>
// //                         </TableRow>
// //                       )}
// //                     </TableBody>
// //                   </Table>
// //                 </TableContainer>
// //                 <TablePagination
// //                   rowsPerPageOptions={[5, 10, 25, 50]}
// //                   component="div"
// //                   count={filteredUsers.length}
// //                   rowsPerPage={rowsPerPage}
// //                   page={page}
// //                   onPageChange={handleChangePage}
// //                   onRowsPerPageChange={handleChangeRowsPerPage}
// //                   sx={{
// //                     borderTop: "1px solid",
// //                     borderColor: "#f5f5f5",
// //                     color: "#A9A5A5",
// //                   }}
// //                 />
// //               </>
// //             )}
// //           </Paper>

// //           <Dialog
// //             open={openDialog}
// //             onClose={() => setOpenDialog(false)}
// //             maxWidth="sm"
// //             fullWidth
// //             fullScreen={isMobile}
// //           >
// //             <DialogTitle>
// //               {selectedUser ? "Edit User" : "Add New User"}
// //             </DialogTitle>
// //             <DialogContent>
// //               <Grid container spacing={2} sx={{ mt: 1 }}>
// //                 <Grid item xs={12}>
// //                   <TextField
// //                     fullWidth
// //                     label="Username"
// //                     value={newUser.username}
// //                     onChange={(e) =>
// //                       setNewUser((p) => ({ ...p, username: e.target.value }))
// //                     }
// //                   />
// //                 </Grid>
// //                 <Grid item xs={12}>
// //                   <TextField
// //                     fullWidth
// //                     label="Email Address"
// //                     type="email"
// //                     value={newUser.email}
// //                     onChange={(e) =>
// //                       setNewUser((p) => ({ ...p, email: e.target.value }))
// //                     }
// //                   />
// //                 </Grid>
// //                 <Grid item xs={12}>
// //                   <TextField
// //                     fullWidth
// //                     label="Phone Number"
// //                     value={newUser.phone_number}
// //                     onChange={(e) =>
// //                       setNewUser((p) => ({
// //                         ...p,
// //                         phone_number: e.target.value,
// //                       }))
// //                     }
// //                   />
// //                 </Grid>
// //                 <Grid item xs={12}>
// //                   <TextField
// //                     fullWidth
// //                     label="Password"
// //                     type="password"
// //                     helperText={
// //                       selectedUser
// //                         ? "Leave blank to keep current password"
// //                         : "Required for new users"
// //                     }
// //                     value={newUser.password}
// //                     onChange={(e) =>
// //                       setNewUser((p) => ({ ...p, password: e.target.value }))
// //                     }
// //                   />
// //                 </Grid>
// //                 <Grid item xs={12}>
// //                   <FormControl fullWidth>
// //                     <InputLabel>Role</InputLabel>
// //                     <Select
// //                       value={newUser.user_role}
// //                       onChange={(e) =>
// //                         setNewUser((p) => ({ ...p, user_role: e.target.value }))
// //                       }
// //                       label="Role"
// //                     >
// //                       <MenuItem value="talent">Talent</MenuItem>
// //                       <MenuItem value="employer">Employee</MenuItem>
// //                       <MenuItem value="admin">Admin</MenuItem>
// //                     </Select>
// //                   </FormControl>
// //                 </Grid>
// //               </Grid>
// //             </DialogContent>
// //             <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
// //               <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
// //               <Button
// //                 variant="contained"
// //                 onClick={handleSaveUser}
// //                 sx={{ backgroundColor: "#4F46BA" }}
// //               >
// //                 {selectedUser ? "Update User" : "Create User"}
// //               </Button>
// //             </DialogActions>
// //           </Dialog>

// //           <Dialog
// //             open={deleteConfirm.open}
// //             onClose={handleCloseDeleteConfirm}
// //             maxWidth="xs"
// //             fullWidth
// //           >
// //             <DialogTitle>Confirm Deletion</DialogTitle>
// //             <DialogContent>
// //               <Typography>
// //                 Are you sure you want to delete the user "
// //                 {deleteConfirm.user?.username}"? This action cannot be undone.
// //               </Typography>
// //             </DialogContent>
// //             <DialogActions>
// //               <Button onClick={() => handleCloseDeleteConfirm()}>Cancel</Button>
// //               <Button
// //                 variant="contained"
// //                 color="error"
// //                 onClick={handleDeleteConfirm}
// //               >
// //                 Delete
// //               </Button>
// //             </DialogActions>
// //           </Dialog>

// //           {viewingUser && (
// //             <Dialog
// //               open={viewUserDialogOpen}
// //               onClose={() => setViewUserDialogOpen(false)}
// //               maxWidth="md"
// //               fullWidth
// //               fullScreen={isMobile}
// //             >
// //               <DialogTitle sx={{ p: 2, bgcolor: "background.paper" }}>
// //                 <Stack
// //                   direction="row"
// //                   justifyContent="space-between"
// //                   alignItems="center"
// //                 >
// //                   <Typography variant="h6" fontWeight={600}>
// //                     User Details
// //                   </Typography>
// //                   <IconButton onClick={() => setViewUserDialogOpen(false)}>
// //                     <Close />
// //                   </IconButton>
// //                 </Stack>
// //               </DialogTitle>
// //               <Divider />
// //               <DialogContent
// //                 sx={{ p: { xs: 2, sm: 3 }, bgcolor: "action.hover" }}
// //               >
// //                 <Grid container spacing={3}>
// //                   <Grid item xs={12} md={4}>
// //                     <Paper
// //                       elevation={0}
// //                       sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}
// //                     >
// //                       <Avatar
// //                         sx={{
// //                           width: 100,
// //                           height: 100,
// //                           fontSize: "3rem",
// //                           mx: "auto",
// //                           mb: 2,
// //                           bgcolor: getRoleChipStyle(viewingUser.user_role)
// //                             .color,
// //                         }}
// //                       >
// //                         {viewingUser.username.charAt(0).toUpperCase()}
// //                       </Avatar>
// //                       <Typography variant="h5" fontWeight={700}>
// //                         {viewingUser.username}
// //                       </Typography>
// //                       <Typography color="text.secondary" gutterBottom>
// //                         {viewingUser.email}
// //                       </Typography>
// //                       <Chip
// //                         label={viewingUser.user_role}
// //                         size="small"
// //                         sx={{
// //                           ...getRoleChipStyle(viewingUser.user_role),
// //                           fontWeight: 600,
// //                           borderRadius: "6px",
// //                           textTransform: "capitalize",
// //                           mt: 1,
// //                         }}
// //                       />
// //                     </Paper>
// //                   </Grid>
// //                   <Grid item xs={12} md={8}>
// //                     <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
// //                       <Typography variant="h6" fontWeight={600} mb={2}>
// //                         Account Information
// //                       </Typography>
// //                       <List dense>
// //                         <DetailItem
// //                           icon={<Badge />}
// //                           primary="User ID"
// //                           secondary={viewingUser.id}
// //                         />
// //                         <DetailItem
// //                           icon={<Phone />}
// //                           primary="Phone Number"
// //                           secondary={viewingUser.phone_number}
// //                         />
// //                         <DetailItem
// //                           icon={
// //                             viewingUser.is_active ? (
// //                               <TaskAlt color="success" />
// //                             ) : (
// //                               <PendingActions color="warning" />
// //                             )
// //                           }
// //                           primary="Status"
// //                           secondary={
// //                             viewingUser.is_active
// //                               ? "Active"
// //                               : "Inactive / Pending"
// //                           }
// //                         />
// //                         <DetailItem
// //                           icon={<CalendarToday />}
// //                           primary="Date Joined"
// //                           secondary={formatDate(viewingUser.date_joined)}
// //                         />
// //                         <DetailItem
// //                           icon={<Login />}
// //                           primary="Last Login"
// //                           secondary={formatDate(viewingUser.last_login)}
// //                         />
// //                       </List>
// //                     </Paper>
// //                   </Grid>
// //                   <Grid item xs={12} md={8}>
// //                     <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
// //                       <Typography variant="h6" fontWeight={600} mb={2}>
// //                         Roles & Permissions
// //                       </Typography>
// //                       <List dense>
// //                         <DetailItem
// //                           icon={
// //                             viewingUser.is_staff ? (
// //                               <CheckCircle color="success" />
// //                             ) : (
// //                               <Cancel color="error" />
// //                             )
// //                           }
// //                           primary="Staff Access (Admin)"
// //                           secondary={viewingUser.is_staff ? "Yes" : "No"}
// //                         />
// //                         <ListItem disablePadding>
// //                           <ListItemIcon
// //                             sx={{ minWidth: 40, color: "text.secondary" }}
// //                           >
// //                             <VerifiedUser />
// //                           </ListItemIcon>
// //                           <ListItemText
// //                             primary={
// //                               <Typography
// //                                 variant="body2"
// //                                 color="text.secondary"
// //                               >
// //                                 User Permissions
// //                               </Typography>
// //                             }
// //                             secondary={
// //                               viewingUser.user_permissions.length > 0 ? (
// //                                 <Stack
// //                                   direction="row"
// //                                   spacing={1}
// //                                   flexWrap="wrap"
// //                                   mt={0.5}
// //                                 >
// //                                   {viewingUser.user_permissions.map((p) => (
// //                                     <Chip key={p} label={p} size="small" />
// //                                   ))}
// //                                 </Stack>
// //                               ) : (
// //                                 "None"
// //                               )
// //                             }
// //                           />
// //                         </ListItem>
// //                       </List>
// //                     </Paper>
// //                   </Grid>
// //                 </Grid>
// //               </DialogContent>
// //             </Dialog>
// //           )}
// //         </Container>
// //       </Box>
// //     </Layout>
// //   );
// // };

// // export default UserManagement;
// // "use client";

// // import { useState, useEffect, useCallback } from "react";
// // import {
// //   Grid,
// //   Card,
// //   CardContent,
// //   Typography,
// //   Button,
// //   Box,
// //   Table,
// //   TableBody,
// //   TableCell,
// //   TableContainer,
// //   TableHead,
// //   TableRow,
// //   Avatar,
// //   IconButton,
// //   Dialog,
// //   DialogTitle,
// //   DialogContent,
// //   DialogActions,
// //   TextField,
// //   FormControl,
// //   InputLabel,
// //   Select,
// //   MenuItem,
// //   Chip,
// //   Stack,
// //   Paper,
// //   useTheme,
// //   useMediaQuery,
// //   TablePagination,
// //   Divider,
// //   ListItem,
// //   ListItemText,
// //   ListItemIcon,
// //   List,
// //   InputAdornment,
// //   Skeleton,
// // } from "@mui/material";
// // import { alpha } from "@mui/material/styles";
// // import {
// //   Add,
// //   Edit,
// //   Delete,
// //   Search,
// //   Phone,
// //   Close,
// //   Badge,
// //   CalendarToday,
// //   Login,
// //   TaskAlt,
// //   PendingActions,
// //   VerifiedUser,
// //   KeyboardArrowDown,
// // } from "@mui/icons-material";
// // import Layout from "../common/Layout";
// // import { useNotification } from "../../context/NotificationContext";
// // import apiClient from "../../context/apiClient";

// // const DetailItem = ({ icon, primary, secondary }) => (
// //   <ListItem disablePadding>
// //     <ListItemIcon sx={{ minWidth: 40, color: "text.secondary" }}>
// //       {icon}
// //     </ListItemIcon>
// //     <ListItemText
// //       primary={
// //         <Typography variant="body2" color="text.secondary">
// //           {primary}
// //         </Typography>
// //       }
// //       secondary={
// //         <Typography variant="body1" fontWeight={500} color="text.primary">
// //           {secondary || "N/A"}
// //         </Typography>
// //       }
// //     />
// //   </ListItem>
// // );

// // const UserManagement = () => {
// //   const { showNotification } = useNotification();
// //   const theme = useTheme();
// //   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

// //   const [users, setUsers] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [openDialog, setOpenDialog] = useState(false);
// //   const [deleteConfirm, setDeleteConfirm] = useState({
// //     open: false,
// //     user: null,
// //   });
// //   const [selectedUser, setSelectedUser] = useState(null);
// //   const [searchTerm, setSearchTerm] = useState("");
// //   const [newUser, setNewUser] = useState({
// //     username: "",
// //     email: "",
// //     password: "",
// //     user_role: "talent",
// //     phone_number: "",
// //   });
// //   const [page, setPage] = useState(0);
// //   const [rowsPerPage, setRowsPerPage] = useState(5);
// //   const [viewUserDialogOpen, setViewUserDialogOpen] = useState(false);
// //   const [viewingUser, setViewingUser] = useState(null);

// //   const getRoleChipStyle = useCallback(
// //     (role) => {
// //       switch (role?.toLowerCase()) {
// //         case "talent":
// //           return {
// //             bgcolor: alpha(theme.palette.success.main, 0.1),
// //             color: theme.palette.success.dark || theme.palette.success.main,
// //           };
// //         case "employee":
// //         case "employer":
// //           return {
// //             bgcolor: alpha(theme.palette.warning.main, 0.1),
// //             color: theme.palette.warning.dark || theme.palette.warning.main,
// //           };
// //         case "admin":
// //           return {
// //             bgcolor: alpha(theme.palette.error.main, 0.1),
// //             color: theme.palette.error.dark || theme.palette.error.main,
// //           };
// //         default:
// //           return {
// //             bgcolor: alpha(theme.palette.text.secondary, 0.1),
// //             color: theme.palette.text.secondary,
// //           };
// //       }
// //     },
// //     [theme]
// //   );

// //   const formatDate = (isoString) => {
// //     if (!isoString) return "N/A";
// //     const date = new Date(isoString);
// //     return date.toLocaleDateString("en-GB"); // Formats as DD/MM/YYYY
// //   };

// //   const fetchUsers = useCallback(async () => {
// //     setLoading(true);
// //     try {
// //       const response = await apiClient.get("/admin/users/");
// //       setUsers(response.data);
// //     } catch (error) {
// //       console.error("Failed to fetch users:", error);
// //       const errorMessage =
// //         error.response?.data?.detail ||
// //         "An error occurred while fetching users.";
// //       showNotification(errorMessage, "error");
// //     } finally {
// //       setLoading(false);
// //     }
// //   }, [showNotification]);

// //   useEffect(() => {
// //     fetchUsers();
// //   }, [fetchUsers]);

// //   const handleAddUser = () => {
// //     setSelectedUser(null);
// //     setNewUser({
// //       username: "",
// //       email: "",
// //       password: "",
// //       user_role: "talent",
// //       phone_number: "",
// //     });
// //     setOpenDialog(true);
// //   };

// //   const handleEditUser = (user) => {
// //     setSelectedUser(user);
// //     setNewUser({
// //       username: user.username,
// //       email: user.email,
// //       user_role: user.user_role.toLowerCase(),
// //       phone_number: user.phone_number || "",
// //       password: "",
// //     });
// //     setOpenDialog(true);
// //   };

// //   const handleViewUser = (user) => {
// //     setViewingUser(user);
// //     setViewUserDialogOpen(true);
// //   };

// //   const handleDeleteClick = (user) => setDeleteConfirm({ open: true, user });
// //   const handleCloseDeleteConfirm = () =>
// //     setDeleteConfirm({ open: false, user: null });

// //   const handleDeleteConfirm = async () => {
// //     if (!deleteConfirm.user) return;
// //     try {
// //       await apiClient.delete(`/admin/users/${deleteConfirm.user.id}/`);
// //       showNotification(
// //         `User "${deleteConfirm.user.username}" deleted successfully!`,
// //         "success"
// //       );
// //       handleCloseDeleteConfirm();
// //       fetchUsers();
// //     } catch (error) {
// //       console.error("Failed to delete user:", error);
// //       showNotification(
// //         error.response?.data?.detail || "Failed to delete user.",
// //         "error"
// //       );
// //     }
// //   };

// //   const handleSaveUser = async () => {
// //     try {
// //       const role = newUser.user_role.toUpperCase();
// //       if (selectedUser) {
// //         const payload = {
// //           username: newUser.username,
// //           email: newUser.email,
// //           user_role: role,
// //           phone_number: newUser.phone_number,
// //         };
// //         if (newUser.password) payload.password = newUser.password;
// //         await apiClient.patch(`/admin/users/${selectedUser.id}/`, payload);
// //         showNotification("User updated successfully!", "success");
// //       } else {
// //         if (!newUser.password) {
// //           showNotification("Password is required for new users.", "warning");
// //           return;
// //         }
// //         await apiClient.post("/admin/users/", { ...newUser, user_role: role });
// //         showNotification("User created successfully!", "success");
// //       }
// //       setOpenDialog(false);
// //       fetchUsers();
// //     } catch (error) {
// //       console.error("Failed to save user:", error);
// //       const errors = error.response?.data;
// //       let errorMessage = "An error occurred.";
// //       if (typeof errors === "object" && errors !== null) {
// //         errorMessage = Object.entries(errors)
// //           .map(
// //             ([key, value]) =>
// //               `${key}: ${Array.isArray(value) ? value.join(", ") : value}`
// //           )
// //           .join(" | ");
// //       } else if (errors) {
// //         errorMessage = errors.toString();
// //       }
// //       showNotification(errorMessage, "error");
// //     }
// //   };

// //   const handleChangePage = (event, newPage) => setPage(newPage);
// //   const handleChangeRowsPerPage = (event) => {
// //     setRowsPerPage(parseInt(event.target.value, 10));
// //     setPage(0);
// //   };

// //   const filteredUsers = users.filter(
// //     (user) =>
// //       user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
// //       user.email.toLowerCase().includes(searchTerm.toLowerCase())
// //   );

// //   const userStats = {
// //     total: users.length,
// //     talent: users.filter((u) => u.user_role === "TALENT").length,
// //     employee: users.filter((u) => u.user_role === "EMPLOYER").length,
// //     admin: users.filter((u) => u.user_role === "ADMIN").length,
// //     active: users.filter((u) => u.is_active).length,
// //     pending: users.filter((u) => !u.is_active).length,
// //   };

// //   const sectionPaperStyles = {
// //     p: { xs: 2, md: 3 },
// //     borderRadius: "12px",
// //     backgroundColor: theme.palette.mint.main,
// //     boxShadow: theme.shadows[1],
// //   };

// //   return (
// //     <Layout title="User Management">
// //       <Box
// //         sx={{
// //           p: { xs: 2, md: 3 },
// //           backgroundColor: theme.palette.background.default,
// //         }}
// //       >
// //         <Stack spacing={3}>
// //           {/* ROW 1: HEADER */}
// //           <Paper
// //             elevation={0}
// //             sx={{
// //               ...sectionPaperStyles,
// //               display: "flex",
// //               flexDirection: { xs: "column", md: "row" },
// //               justifyContent: "space-between",
// //               alignItems: { xs: "flex-start", md: "center" },
// //             }}
// //           >
// //             <Box>
// //               <Typography
// //                 variant="h4"
// //                 sx={{ fontWeight: 700, color: theme.palette.primary.main }}
// //               >
// //                 User Management
// //               </Typography>
// //               <Typography variant="body1" color="text.secondary">
// //                 Manage all users across the GATEP Platform
// //               </Typography>
// //             </Box>
// //             <Button
// //               variant="contained"
// //               color="primary"
// //               startIcon={<Add />}
// //               onClick={handleAddUser}
// //               sx={{ mt: { xs: 2, md: 0 } }}
// //             >
// //               Add New User
// //             </Button>
// //           </Paper>

// //           {/* ROW 2: SEARCH & FILTERS */}
// //           <Paper
// //             elevation={0}
// //             sx={{
// //               p: 2,
// //               borderRadius: "12px",
// //               backgroundColor: "background.paper",
// //               border: `1px solid ${theme.palette.divider}`,
// //             }}
// //           >
// //             <Stack
// //               direction={{ xs: "column", md: "row" }}
// //               spacing={2}
// //               alignItems="center"
// //             >
// //               <TextField
// //                 fullWidth
// //                 placeholder="Search by username or email"
// //                 value={searchTerm}
// //                 onChange={(e) => setSearchTerm(e.target.value)}
// //                 InputProps={{
// //                   startAdornment: (
// //                     <InputAdornment position="start">
// //                       <Search color="disabled" />
// //                     </InputAdornment>
// //                   ),
// //                 }}
// //               />
// //               {/* Add optional filter Dropdowns here if needed */}
// //             </Stack>
// //           </Paper>

// //           {/* ROW 3: STAT CARDS */}
// //           <Box
// //             sx={{
// //               display: "flex",
// //               gap: { xs: 1.5, md: 2 },
// //               flexWrap: { xs: "wrap", md: "nowrap" },
// //             }}
// //           >
// //             {(loading
// //               ? [...Array(6)]
// //               : [
// //                   { title: "Total Users", value: userStats.total },
// //                   { title: "AI Talent", value: userStats.talent },
// //                   { title: "Employers", value: userStats.employee },
// //                   { title: "Admins", value: userStats.admin },
// //                   { title: "Active", value: userStats.active },
// //                   { title: "Inactive", value: userStats.pending },
// //                 ]
// //             ).map((stat, index) => (
// //               <Box
// //                 key={index}
// //                 sx={{
// //                   flex: { xs: "1 1 45%", md: 1 },
// //                   minWidth: { xs: 150, md: 0 },
// //                 }}
// //               >
// //                 {loading ? (
// //                   <Skeleton
// //                     variant="rectangular"
// //                     height={120}
// //                     sx={{ borderRadius: "14px" }}
// //                   />
// //                 ) : (
// //                   <Card
// //                     sx={{
// //                       position: "relative",
// //                       backgroundColor: theme.palette.primary.main,
// //                       color: theme.palette.primary.contrastText,
// //                       height: 120,
// //                       borderRadius: "14px",
// //                       overflow: "hidden",
// //                       boxShadow: theme.shadows[2],
// //                     }}
// //                   >
// //                     <svg
// //                       width="100"
// //                       height="120"
// //                       viewBox="0 0 100 120"
// //                       style={{ position: "absolute", right: 0, top: 0 }}
// //                     >
// //                       <path
// //                         d="M40 0 L100 0 L100 120 C 80 120, 70 90, 70 60 C 70 30, 80 0, 40 0 Z"
// //                         fill={theme.palette.accent.main}
// //                       />
// //                     </svg>
// //                     <CardContent
// //                       sx={{
// //                         position: "relative",
// //                         zIndex: 1,
// //                         p: { xs: 1.5, md: 2 },
// //                         display: "flex",
// //                         flexDirection: "column",
// //                         justifyContent: "space-between",
// //                         height: "100%",
// //                       }}
// //                     >
// //                       <Typography
// //                         sx={{
// //                           fontWeight: 600,
// //                           fontSize: { xs: "0.75rem", md: "0.8rem" },
// //                           textTransform: "uppercase",
// //                           color: alpha(theme.palette.primary.contrastText, 0.8),
// //                         }}
// //                       >
// //                         {stat.title}
// //                       </Typography>
// //                       <Typography
// //                         sx={{
// //                           fontWeight: "bold",
// //                           fontSize: { xs: "2rem", md: "2.25rem" },
// //                           lineHeight: 1,
// //                         }}
// //                       >
// //                         {stat.value}
// //                       </Typography>
// //                     </CardContent>
// //                   </Card>
// //                 )}
// //               </Box>
// //             ))}
// //           </Box>

// //           {/* ROW 4: USERS TABLE */}
// //           <Paper
// //             elevation={0}
// //             sx={{
// //               borderRadius: "16px",
// //               overflow: "hidden",
// //               backgroundColor: "background.paper",
// //               border: `1px solid ${theme.palette.divider}`,
// //             }}
// //           >
// //             <TableContainer>
// //               <Table sx={{ minWidth: 800 }}>
// //                 <TableHead>
// //                   <TableRow>
// //                     {[
// //                       "User",
// //                       "Role",
// //                       "Status",
// //                       "Join Date",
// //                       "Last Login",
// //                       "Actions",
// //                     ].map((headCell) => (
// //                       <TableCell key={headCell}>{headCell}</TableCell>
// //                     ))}
// //                   </TableRow>
// //                 </TableHead>
// //                 <TableBody>
// //                   {loading
// //                     ? Array.from(new Array(rowsPerPage)).map((_, index) => (
// //                         <TableRow key={index}>
// //                           <TableCell>
// //                             <Stack
// //                               direction="row"
// //                               alignItems="center"
// //                               spacing={2}
// //                             >
// //                               <Skeleton
// //                                 variant="circular"
// //                                 width={40}
// //                                 height={40}
// //                               />
// //                               <Box>
// //                                 <Skeleton width={80} />
// //                                 <Skeleton width={120} />
// //                               </Box>
// //                             </Stack>
// //                           </TableCell>
// //                           <TableCell>
// //                             <Skeleton width={80} height={24} />
// //                           </TableCell>
// //                           <TableCell>
// //                             <Skeleton width={70} height={24} />
// //                           </TableCell>
// //                           <TableCell>
// //                             <Skeleton width={90} />
// //                           </TableCell>
// //                           <TableCell>
// //                             <Skeleton width={90} />
// //                           </TableCell>
// //                           <TableCell>
// //                             <Stack direction="row">
// //                               <Skeleton
// //                                 variant="circular"
// //                                 width={32}
// //                                 height={32}
// //                               />
// //                               <Skeleton
// //                                 variant="circular"
// //                                 width={32}
// //                                 height={32}
// //                               />
// //                             </Stack>
// //                           </TableCell>
// //                         </TableRow>
// //                       ))
// //                     : filteredUsers
// //                         .slice(
// //                           page * rowsPerPage,
// //                           page * rowsPerPage + rowsPerPage
// //                         )
// //                         .map((user) => (
// //                           <TableRow key={user.id} hover>
// //                             <TableCell>
// //                               <Stack
// //                                 direction="row"
// //                                 alignItems="center"
// //                                 spacing={2}
// //                                 sx={{ cursor: "pointer" }}
// //                                 onClick={() => handleViewUser(user)}
// //                               >
// //                                 <Avatar
// //                                   sx={{
// //                                     bgcolor: getRoleChipStyle(user.user_role)
// //                                       .color,
// //                                   }}
// //                                 >
// //                                   {user.username.charAt(0).toUpperCase()}
// //                                 </Avatar>
// //                                 <Box>
// //                                   <Typography variant="body2" fontWeight={600}>
// //                                     {user.username}
// //                                   </Typography>
// //                                   <Typography
// //                                     variant="caption"
// //                                     color="text.secondary"
// //                                   >
// //                                     {user.email}
// //                                   </Typography>
// //                                 </Box>
// //                               </Stack>
// //                             </TableCell>
// //                             <TableCell>
// //                               <Chip
// //                                 label={user.user_role}
// //                                 size="small"
// //                                 sx={{
// //                                   ...getRoleChipStyle(user.user_role),
// //                                   textTransform: "capitalize",
// //                                 }}
// //                               />
// //                             </TableCell>
// //                             <TableCell>
// //                               <Chip
// //                                 label={user.is_active ? "Active" : "Inactive"}
// //                                 size="small"
// //                                 color={user.is_active ? "success" : "warning"}
// //                                 variant="outlined"
// //                               />
// //                             </TableCell>
// //                             <TableCell>
// //                               {formatDate(user.date_joined)}
// //                             </TableCell>
// //                             <TableCell>{formatDate(user.last_login)}</TableCell>
// //                             <TableCell>
// //                               <Stack direction="row" spacing={0}>
// //                                 <IconButton
// //                                   size="small"
// //                                   onClick={() => handleEditUser(user)}
// //                                 >
// //                                   <Edit fontSize="small" color="primary" />
// //                                 </IconButton>
// //                                 <IconButton
// //                                   size="small"
// //                                   onClick={() => handleDeleteClick(user)}
// //                                 >
// //                                   <Delete fontSize="small" color="disabled" />
// //                                 </IconButton>
// //                               </Stack>
// //                             </TableCell>
// //                           </TableRow>
// //                         ))}
// //                   {!loading && filteredUsers.length === 0 && (
// //                     <TableRow>
// //                       <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
// //                         <Typography>No users found.</Typography>
// //                       </TableCell>
// //                     </TableRow>
// //                   )}
// //                 </TableBody>
// //               </Table>
// //             </TableContainer>
// //             <TablePagination
// //               rowsPerPageOptions={[5, 10, 25, 50]}
// //               component="div"
// //               count={filteredUsers.length}
// //               rowsPerPage={rowsPerPage}
// //               page={page}
// //               onPageChange={handleChangePage}
// //               onRowsPerPageChange={handleChangeRowsPerPage}
// //             />
// //           </Paper>
// //         </Stack>
// //       </Box>

// //       {/* DIALOGS */}
// //       <Dialog
// //         open={openDialog}
// //         onClose={() => setOpenDialog(false)}
// //         maxWidth="sm"
// //         fullWidth
// //         fullScreen={isMobile}
// //       >
// //         <DialogTitle>{selectedUser ? "Edit User" : "Add New User"}</DialogTitle>
// //         <DialogContent>
// //           <Grid container spacing={2} sx={{ mt: 1 }}>
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Username"
// //                 value={newUser.username}
// //                 onChange={(e) =>
// //                   setNewUser((p) => ({ ...p, username: e.target.value }))
// //                 }
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Email Address"
// //                 type="email"
// //                 value={newUser.email}
// //                 onChange={(e) =>
// //                   setNewUser((p) => ({ ...p, email: e.target.value }))
// //                 }
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Phone Number"
// //                 value={newUser.phone_number}
// //                 onChange={(e) =>
// //                   setNewUser((p) => ({ ...p, phone_number: e.target.value }))
// //                 }
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <TextField
// //                 fullWidth
// //                 label="Password"
// //                 type="password"
// //                 helperText={
// //                   selectedUser
// //                     ? "Leave blank to keep current password"
// //                     : "Required for new users"
// //                 }
// //                 value={newUser.password}
// //                 onChange={(e) =>
// //                   setNewUser((p) => ({ ...p, password: e.target.value }))
// //                 }
// //               />
// //             </Grid>
// //             <Grid item xs={12}>
// //               <FormControl fullWidth>
// //                 <InputLabel>Role</InputLabel>
// //                 <Select
// //                   value={newUser.user_role}
// //                   onChange={(e) =>
// //                     setNewUser((p) => ({ ...p, user_role: e.target.value }))
// //                   }
// //                   label="Role"
// //                 >
// //                   <MenuItem value="talent">Talent</MenuItem>
// //                   <MenuItem value="employer">Employer</MenuItem>
// //                   <MenuItem value="admin">Admin</MenuItem>
// //                 </Select>
// //               </FormControl>
// //             </Grid>
// //           </Grid>
// //         </DialogContent>
// //         <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
// //           <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
// //           <Button variant="contained" color="primary" onClick={handleSaveUser}>
// //             {selectedUser ? "Update User" : "Create User"}
// //           </Button>
// //         </DialogActions>
// //       </Dialog>
// //       <Dialog
// //         open={deleteConfirm.open}
// //         onClose={handleCloseDeleteConfirm}
// //         maxWidth="xs"
// //         fullWidth
// //       >
// //         <DialogTitle>Confirm Deletion</DialogTitle>
// //         <DialogContent>
// //           <Typography>
// //             Are you sure you want to delete the user "
// //             {deleteConfirm.user?.username}"? This action cannot be undone.
// //           </Typography>
// //         </DialogContent>
// //         <DialogActions>
// //           <Button onClick={handleCloseDeleteConfirm}>Cancel</Button>
// //           <Button
// //             variant="contained"
// //             color="error"
// //             onClick={handleDeleteConfirm}
// //           >
// //             Delete
// //           </Button>
// //         </DialogActions>
// //       </Dialog>
// //       {viewingUser && (
// //         <Dialog
// //           open={viewUserDialogOpen}
// //           onClose={() => setViewUserDialogOpen(false)}
// //           maxWidth="md"
// //           fullWidth
// //           fullScreen={isMobile}
// //         >
// //           <DialogTitle sx={{ p: 2, bgcolor: "background.paper" }}>
// //             <Stack
// //               direction="row"
// //               justifyContent="space-between"
// //               alignItems="center"
// //             >
// //               <Typography variant="h6" fontWeight={600}>
// //                 User Details
// //               </Typography>
// //               <IconButton onClick={() => setViewUserDialogOpen(false)}>
// //                 <Close />
// //               </IconButton>
// //             </Stack>
// //           </DialogTitle>
// //           <Divider />
// //           <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "action.hover" }}>
// //             <Grid container spacing={3}>
// //               <Grid item xs={12} md={4}>
// //                 <Paper
// //                   elevation={0}
// //                   sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}
// //                 >
// //                   <Avatar
// //                     sx={{
// //                       width: 100,
// //                       height: 100,
// //                       fontSize: "3rem",
// //                       mx: "auto",
// //                       mb: 2,
// //                       bgcolor: getRoleChipStyle(viewingUser.user_role).color,
// //                     }}
// //                   >
// //                     {viewingUser.username.charAt(0).toUpperCase()}
// //                   </Avatar>
// //                   <Typography variant="h5" fontWeight={700}>
// //                     {viewingUser.username}
// //                   </Typography>
// //                   <Typography color="text.secondary" gutterBottom>
// //                     {viewingUser.email}
// //                   </Typography>
// //                   <Chip
// //                     label={viewingUser.user_role}
// //                     size="small"
// //                     sx={{
// //                       ...getRoleChipStyle(viewingUser.user_role),
// //                       fontWeight: 600,
// //                       borderRadius: "6px",
// //                       textTransform: "capitalize",
// //                       mt: 1,
// //                     }}
// //                   />
// //                 </Paper>
// //               </Grid>
// //               <Grid item xs={12} md={8}>
// //                 <Stack spacing={3}>
// //                   <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
// //                     <Typography variant="h6" fontWeight={600} mb={2}>
// //                       Account Information
// //                     </Typography>
// //                     <List dense>
// //                       <DetailItem
// //                         icon={<Badge />}
// //                         primary="User ID"
// //                         secondary={viewingUser.id}
// //                       />
// //                       <DetailItem
// //                         icon={<Phone />}
// //                         primary="Phone Number"
// //                         secondary={viewingUser.phone_number}
// //                       />
// //                       <DetailItem
// //                         icon={
// //                           viewingUser.is_active ? (
// //                             <TaskAlt color="success" />
// //                           ) : (
// //                             <PendingActions color="warning" />
// //                           )
// //                         }
// //                         primary="Status"
// //                         secondary={
// //                           viewingUser.is_active
// //                             ? "Active"
// //                             : "Inactive / Pending"
// //                         }
// //                       />
// //                       <DetailItem
// //                         icon={<CalendarToday />}
// //                         primary="Date Joined"
// //                         secondary={formatDate(viewingUser.date_joined)}
// //                       />
// //                       <DetailItem
// //                         icon={<Login />}
// //                         primary="Last Login"
// //                         secondary={formatDate(viewingUser.last_login)}
// //                       />
// //                     </List>
// //                   </Paper>
// //                   <Paper elevation={0} sx={{ p: 3, borderRadius: "12px" }}>
// //                     <Typography variant="h6" fontWeight={600} mb={2}>
// //                       Roles & Permissions
// //                     </Typography>
// //                     <List dense>
// //                       <DetailItem
// //                         icon={
// //                           viewingUser.is_staff ? (
// //                             <TaskAlt color="success" />
// //                           ) : (
// //                             <PendingActions color="error" />
// //                           )
// //                         }
// //                         primary="Staff Access (Admin)"
// //                         secondary={viewingUser.is_staff ? "Yes" : "No"}
// //                       />
// //                       <ListItem disablePadding>
// //                         <ListItemIcon sx={{ minWidth: 40 }}>
// //                           <VerifiedUser />
// //                         </ListItemIcon>
// //                         <ListItemText
// //                           primary={
// //                             <Typography variant="body2" color="text.secondary">
// //                               User Permissions
// //                             </Typography>
// //                           }
// //                           secondary={
// //                             viewingUser.user_permissions.length > 0 ? (
// //                               <Stack
// //                                 direction="row"
// //                                 spacing={1}
// //                                 flexWrap="wrap"
// //                                 mt={0.5}
// //                               >
// //                                 {viewingUser.user_permissions.map((p) => (
// //                                   <Chip key={p} label={p} size="small" />
// //                                 ))}
// //                               </Stack>
// //                             ) : (
// //                               "None"
// //                             )
// //                           }
// //                         />
// //                       </ListItem>
// //                     </List>
// //                   </Paper>
// //                 </Stack>
// //               </Grid>
// //             </Grid>
// //           </DialogContent>
// //         </Dialog>
// //       )}
// //     </Layout>
// //   );
// // };

// // export default UserManagement;
